import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { signOut } from 'firebase/auth';
import { collection, getDocs, query, where, limit, addDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { useOnboardingQuickstart } from '../contexts/OnboardingQuickstartContext';
import Footer from '../components/Footer';
import ListingThumb from '../components/ListingThumb';
import { getPedalsFromUsersWhoWantMine, enrichPedalsWithOwnerData, fetchWantsMyPedalsRows } from '../utils/recommendations';
import { isGiveawayPromoEnabled } from '../featureFlags';
import { getWantListMatchesFromServer } from '../utils/wantListMatches';
import { listingListedAtMs } from '../utils/sortPedalsByListedAt';
import { createSingleFlight } from '../utils/singleFlight';
import { runVerificationCheck } from '../utils/verificationCheck';
import { runWithTimeoutAndRetry, runWithSoftTimeout, armRecoveryOnReconnect, throwIfOfflineEmpty } from '../utils/recoverableLoad';
import { fetchPublicProfiles } from '../utils/publicProfilesBatch';
import {
  markFirstVisitIfNeeded,
  getMySaleOnlyPedalIds,
  formatBadgeCount,
  subscribeToMatchCounters
} from '../utils/dashboardMatchCounters';
import SEO from '../components/SEO';
import AccountChecklist from '../components/AccountChecklist';
import ActiveTraderBadge from '../components/ActiveTraderBadge';
import PedalPriceDisplay from '../components/PedalPriceDisplay';
import RecommendedPedalCard from '../components/RecommendedPedalCard';
import ActiveTradesPurchasesStrip from '../components/dashboard/ActiveTradesPurchasesStrip';
import DashboardPedalNewsStrip from '../components/dashboard/DashboardPedalNewsStrip';
import SellingDisabledBanner from '../components/SellingDisabledBanner';
import DashboardAnnouncementBanner from '../components/DashboardAnnouncementBanner';
import { useRegisterPullToRefresh } from '../hooks/useRegisterPullToRefresh';
import './dashboardSectionHeaders.css';
import { reloadPage } from '../utils/reloadPage';
import { placeholderFor, listingImageUrls, isPlaceholderListing, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { BLANK_IMAGE_SRC, createRevealedSlides } from '../utils/carouselSlideWindow';
import { perfMark } from '../utils/perfMarks';
import { readRecommendedCache, writeRecommendedCache } from '../utils/recommendedCache';
import { allowsTrade } from '../utils/offerMode';
import OnboardingTip from '../components/OnboardingTip';
import useOnboardingTips from '../hooks/useOnboardingTips';

// Phase 2c (2026-09-29): shared whole-loader timeout + one-retry bounds for
// the dashboard's three user-facing section loaders (same values Browse uses).
const DASH_FETCH_TIMEOUT_MS = 10_000;
const DASH_RETRY_DELAY_MS = 1_000;
// Trade Opportunities / Saved Pedals (2026-10-31, cellular failure): one
// attempt, "still loading" after DASH_SLOW_MS, terminal only at DASH_HARD_MS.
// See runWithSoftTimeout in utils/recoverableLoad.js for why the 10 s
// timeout-plus-retry produced "Couldn't load this section" on a slow but
// working connection.
const DASH_SLOW_MS = 8_000;
const DASH_HARD_MS = 40_000;

// Inline couldn't-load state for ONE dashboard section (Phase 2c). Renders in
// place of that section's content only — never page-level, never over loaded
// cards — so a failed carousel can no longer masquerade as the legitimate
// "No matches yet" / "No recommendations yet" empty copy.
// Try again reloads the page (see utils/reloadPage for why).
const SectionLoadFailed = () => (
  // width/margin/flexShrink: the three sections mount this inside different
  // containers (block, and the Just Listed carousel's flex track, where a
  // content-sized child shrinks and parks left) — stretch to the row and
  // self-center so the band reads identically in every section.
  <div className="bg-gray-50 rounded-lg text-center" style={{ padding: '1rem 2rem 1.25rem 2rem', width: '100%', maxWidth: '480px', margin: '0 auto', boxSizing: 'border-box', flexShrink: 0 }}>
    <p className="text-gray-500 text-lg" style={{ marginBottom: '0.75rem' }}>
      Couldn&rsquo;t load this section. Check your connection.
    </p>
    <button
      onClick={() => reloadPage()}
      style={{
        fontFamily: 'Nunito, sans-serif',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        color: 'white',
        backgroundColor: '#4A9FCA',
        border: 'none',
        borderRadius: '50px',
        padding: '0.5rem 1.6rem',
        cursor: 'pointer'
      }}
    >
      Try again
    </button>
  </div>
);

const UserDashboard = ({ user, authResolved = true }) => {
  const [isVerified, setIsVerified] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredPedals, setFeaturedPedals] = useState([]);
  const [isLoadingFeatured, setIsLoadingFeatured] = useState(true);
  // NOTE (2026-09-29): this flag is written by checkUserExperience but is
  // never read by the render — dead state, kept as-is and logged in the
  // follow-up ledger rather than removed mid-recovery-work. The dashboard
  // arrangement is actually driven by loggedInCarouselLayout below.
  const [isExperiencedUser, setIsExperiencedUser] = useState(false);
  // Trade Opportunities cards from the last visit, kept in module memory for
  // this account only (2026-10-31): a return to the dashboard shows the cards
  // it had at once and refreshes them behind; nothing is persisted, the cache
  // holds public listing cards (no transaction records), and it is dropped
  // on sign-out or an account change.
  const [recommendedPedals, setRecommendedPedals] = useState(() => readRecommendedCache(auth.currentUser?.uid));
  // Local timing mark: first time Trade Opportunities cards are in the DOM.
  const recommendedCommitMarkedRef = useRef(false);
  useEffect(() => {
    if (recommendedPedals.length > 0 && !recommendedCommitMarkedRef.current) {
      recommendedCommitMarkedRef.current = true;
      perfMark('pxc:trade-opportunities-commit');
    }
  }, [recommendedPedals.length]);
  // First-visit tips (Jason, 2026-10-20): profile, then Browse, then Trade
  // Opportunities once the member actually has one.
  const { activeTip: dashboardTip, dismiss: dismissDashboardTip, delay: dashboardTipDelay } = useOnboardingTips([
    { id: 'dashboardProfile' },
    { id: 'dashboardBrowse' },
    { id: 'dashboardTradeOpportunities', ready: recommendedPedals.length > 0 },
  ]);
  const [isLoadingRecommended, setIsLoadingRecommended] = useState(() => readRecommendedCache(auth.currentUser?.uid).length === 0);
  const [wantListMatches, setWantListMatches] = useState([]);
  const [isLoadingWantListMatches, setIsLoadingWantListMatches] = useState(true);
  // "X new" badge counters for the carousel headers. Derived from
  // wantsMyPedals docs created since the user last viewed each destination
  // page (see src/utils/dashboardMatchCounters.js for full semantics).
  const [newWantListMatchCount, setNewWantListMatchCount] = useState(0);
  const [newMatchOpportunitiesCount, setNewMatchOpportunitiesCount] = useState(0);
  /* Set of userIds with at least one open `wantsMyPedals` doc against the
     current user. Drives the "Potential Match" banner on the Wantlist
     Matches carousel — if the listing owner also wants one of OUR pedals,
     it's worth flagging even though every card on that carousel is already
     a want-list match in the OTHER direction. Mirrors the page-level
     implementation in WantListMatchesPage / LatestPedals. */
  const [potentialMatchUserIds, setPotentialMatchUserIds] = useState(new Set());
  const [successfulTrades, setSuccessfulTrades] = useState([]);
  const [currentTradeIndex, setCurrentTradeIndex] = useState(0);
  // Measured pixel width of the Recent Trades carousel viewport. Used to compute the slide
  // transform in INTEGER pixels (instead of percentages) so cumulative sub-pixel rounding
  // can never accumulate across many auto-scroll cycles. 0 = pre-measure → falls back to
  // percentage transform on first render until the ResizeObserver fires.
  const [recentTradesSlideWidth, setRecentTradesSlideWidth] = useState(0);
  const [isLoadingTrades, setIsLoadingTrades] = useState(true);
  /** True while Active Trades & Purchases strip is mounted (has items or loading). */
  const [activeTradesStripVisible, setActiveTradesStripVisible] = useState(false);
  const scrollContainerRef = useRef(null);
  const matchScrollContainerRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const wantListScrollContainerRef = useRef(null);
  const recentTradesCarouselRef = useRef(null);
  // The live counter subscription for the current user (see
  // subscribeToMatchCounters). Held in a ref so the [user] effect cleanup
  // and pull-to-refresh can reach it.
  const matchCountersUnsubRef = useRef(null);
  // Which Recent Trades slides have been allowed to load their images.
  // Computed here, before the carousel JSX below is built (the carousel
  // markup is assembled well above the component's return statement).
  // A new slide list means the indexes name different cards, so the set is
  // reset when the list identity changes.
  const revealedSlidesRef = useRef(createRevealedSlides());
  const revealedForListRef = useRef(null);
  if (revealedForListRef.current !== successfulTrades) {
    revealedSlidesRef.current.reset();
    revealedForListRef.current = successfulTrades;
  }
  // Image load window: the current slide, its neighbours (incl. wrap-around),
  // and every slide revealed earlier in this list's lifetime.
  const revealedSlides = revealedSlidesRef.current.advance(currentTradeIndex, successfulTrades.length);
  const { username, firestoreUser } = useContext(UserContext);
  const { openQuickstart } = useOnboardingQuickstart();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );

  /** Latest Pedals row: width must match actual card count (was hardcoded for 8 → empty scroll on mobile). */
  const latestPedalsTrackMinWidth = useMemo(() => {
    /* Mobile uses zoom:0.75 on cards — laid-out width is smaller than 331px; a 331px-based minWidth leaves a large dead zone after the last card. */
    if (isMobile) return undefined;
    const card = 331;
    const gap = 32;
    if (isLoadingFeatured) {
      const n = 4;
      return `${n * card + (n - 1) * gap}px`;
    }
    const n = featuredPedals.length;
    if (n <= 0) return undefined;
    return `${n * card + (n - 1) * gap}px`;
  }, [isLoadingFeatured, featuredPedals.length, isMobile]);

  // Create welcome notification for existing users who already have onboardingComplete: true
  // (New users will get it automatically via Firestore trigger when onboardingComplete changes)
  useEffect(() => {
    const checkAndCreateWelcomeNotification = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || !firestoreUser) return;

      // Only check if user has completed onboarding (all steps 1-7) but might not have notification
      if (!firestoreUser.onboardingComplete) return;

      try {
        // Check if user already has a welcome notification
        const notificationsRef = collection(db, 'users', currentUser.uid, 'notifications');
        const welcomeQuery = query(notificationsRef, where('type', '==', 'welcome'), limit(1));
        const existingWelcome = await getDocs(welcomeQuery);

        if (!existingWelcome.empty) {
          return; // Already has notification
        }

        // Check if user has any pedals
        const pedalsQuery = query(
          collection(db, 'listings'),
          where('ownerId', '==', currentUser.uid),
          where('active', '==', true),
          limit(1)
        );
        const pedalsSnapshot = await getDocs(pedalsQuery);
        const hasPedals = !pedalsSnapshot.empty;

        if (!hasPedals) {
          // Use Cloud Function to create notification (bypasses App Check and security rules)
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const functions = getFunctions();
          const createWelcomeNotificationFn = httpsCallable(functions, 'createWelcomeNotification');
          
          const result = await createWelcomeNotificationFn();
        }
      } catch (error) {
        console.error('❌ Error checking/creating welcome notification:', error);
        // Silently fail - notification will be created by trigger for truly new users
      }
    };

    if (firestoreUser) {
      checkAndCreateWelcomeNotification();
    }
  }, [firestoreUser]);
  
  // Detect mobile screen size and calculate responsive gap and margin
  const [gapSize, setGapSize] = useState('8rem');
  const [missionMarginLeft, setMissionMarginLeft] = useState('8rem');
  
  useEffect(() => {
    const updateLayout = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      
      // Calculate gap and margin based on screen width (desktop only) - more aggressive shrinking
      if (width > 768) {
        if (width >= 1500) {
          setGapSize('8rem');
          setMissionMarginLeft('8rem');
        } else if (width >= 1400) {
          setGapSize('5rem');
          setMissionMarginLeft('5rem');
        } else if (width >= 1300) {
          setGapSize('4rem');
          setMissionMarginLeft('4rem');
        } else if (width >= 1200) {
          setGapSize('3rem');
          setMissionMarginLeft('3rem');
        } else if (width >= 1100) {
          setGapSize('2rem');
          setMissionMarginLeft('2rem');
        } else if (width >= 1000) {
          setGapSize('1.5rem');
          setMissionMarginLeft('1.5rem');
        } else if (width >= 900) {
          setGapSize('1rem');
          setMissionMarginLeft('1rem');
        } else {
          setGapSize('0.75rem');
          setMissionMarginLeft('0.75rem');
        }
      }
    };
    
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const bannerImages = [
    '/images/dashboard_home_2.jpg'
  ];

  // Giveaway hero is WEB-ONLY (Jason, 2026-09-21). The native apps ship a
  // frozen copy of the bundle, so a hosting deploy never reaches them, but an
  // unrelated store release during the giveaway window would drag a
  // time-limited contest into App Store review and leave it in installed apps
  // after the giveaway ends. Native therefore always renders the evergreen
  // hero; only web shows the giveaway promo.
  //
  // isGiveawayPromoEnabled() additionally keeps the promo out of production
  // builds until launch, so unrelated fixes can deploy while the giveaway
  // waits. Remove both gates together with the giveaway image when the
  // giveaway ends.
  const showGiveawayHero = isGiveawayPromoEnabled() && !Capacitor.isNativePlatform();
  const heroImageSrc = showGiveawayHero ? '/images/Giveaway_BM.png' : bannerImages[0];
  const heroImageAlt = showGiveawayHero ? 'Pedal giveaway — details' : 'PedalxChange';
  const heroClick = showGiveawayHero ? () => navigate('/giveaway') : undefined;
  const heroCursor = showGiveawayHero ? 'pointer' : undefined;
  
  const [currentBannerIndex] = useState(0);

  const getMembershipBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            9V Member
          </div>
        );
      case 'premium_plus':
        return (
          <div className="flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            18V Member
          </div>
        );
      default:
        return null;
    }
  };

  const getMembershipProfileBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            9V
          </div>
        );
      case 'premium_plus':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#7c3aed',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            18V
          </div>
        );
      default:
        return null;
    }
  };

  // Render the pedal image(s) for one side of a Recent Trades card.
  // - Single pedal: returns the same <img> as before with the same className/inline styles
  //   (preserves all existing desktop + mobile CSS unchanged).
  // - Multi-pedal (multi-for-one trades only — only one side is ever multi): returns a
  //   wrapper div containing up to 4 diagonally-cascading <img> cards (back→front,
  //   top-left→bottom-right). If pedals.length > 4, a "+N" badge appears on the front card.
  // - All multi-pedal sizes are percentage-based relative to the wrapper, so the existing
  //   mobile media queries that resize the wrapper cause the stack to scale automatically.
  const renderPedalStack = (pedals, side, opts) => {
    const tradeId = opts?.tradeId;
    const isCurrent = !!opts?.isCurrent;
    const isAdjacent = !!opts?.isAdjacent;
    // Slides outside the load window render a blank 1x1 source with the same
    // box, so nothing loads for them and nothing shifts when they do load.
    const withImages = opts?.withImages !== false;
    const imageSrc = (p) => (withImages ? (p.images?.top || placeholderFor(p).url) : BLANK_IMAGE_SRC);
    const cap = 4;
    const visible = pedals.slice(0, cap);
    const overflow = Math.max(0, pedals.length - cap);

    const isLarge = side === 'large';
    const sizeClass = isLarge ? 'recent-trades-large-img' : null;
    const inlineSize = isLarge
      ? { width: '336px', height: '336px' }
      : { width: '202px', height: '202px' };
    const radius = isLarge ? '12px' : '10px';

    // Single-pedal path — preserve exact existing markup so existing CSS keeps working.
    if (visible.length === 1) {
      const p = visible[0];
      return (
        <ListingThumb
          className={sizeClass || undefined}
          key={`${side}-${tradeId}-${p.images?.top || 'ph'}`}
          src={imageSrc(p)}
          fallbackSrc={SKETCH_PLACEHOLDER_URL}
          alt={`${p.make || ''} ${p.model || ''}`.trim() || 'Pedal'}
          loading={isCurrent || isAdjacent ? 'eager' : 'lazy'}
          fetchpriority={isCurrent ? 'high' : 'auto'}
          style={{ ...inlineSize, objectFit: 'cover', borderRadius: radius }}
        />
      );
    }

    // Multi-pedal stack layout: scalePct + (visible.length - 1) * offsetPct = 100 so the
    // front card always flushes the wrapper's bottom-right corner.
    const stackLayoutByCount = {
      2: { scalePct: 70, offsetPct: 30 },
      3: { scalePct: 60, offsetPct: 20 },
      4: { scalePct: 55, offsetPct: 15 },
    };
    const { scalePct, offsetPct } = stackLayoutByCount[visible.length] || stackLayoutByCount[4];
    const stackClass = isLarge ? 'recent-trades-stack-large' : 'recent-trades-stack-small';

    return (
      <div
        className={stackClass}
        style={{ ...inlineSize, position: 'relative', overflow: 'visible' }}
      >
        {visible.map((p, i) => (
          <ListingThumb
            key={`${side}-${tradeId}-stack-${i}-${p.images?.top || 'ph'}`}
            src={imageSrc(p)}
            fallbackSrc={SKETCH_PLACEHOLDER_URL}
            alt={`${p.make || ''} ${p.model || ''}`.trim() || 'Pedal'}
            loading={isCurrent || isAdjacent ? 'eager' : 'lazy'}
            // Only the front (most-visible) card gets fetchpriority high — the back cards are partially occluded
            fetchpriority={isCurrent && i === visible.length - 1 ? 'high' : 'auto'}
            style={{
              position: 'absolute',
              left: `${i * offsetPct}%`,
              top: `${i * offsetPct}%`,
              width: `${scalePct}%`,
              height: `${scalePct}%`,
              objectFit: 'cover',
              borderRadius: radius,
              border: '2px solid #ffffff',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.18)',
              zIndex: i + 1,
              backgroundColor: '#ffffff'
            }}
          />
        ))}
        {overflow > 0 && (
          <div
            aria-label={`${overflow} additional pedal${overflow > 1 ? 's' : ''}`}
            style={{
              position: 'absolute',
              right: '6px',
              bottom: '6px',
              minWidth: '28px',
              height: '28px',
              padding: '0 6px',
              borderRadius: '14px',
              backgroundColor: '#374151',
              color: '#ffffff',
              fontSize: '0.75rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #ffffff',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              zIndex: 100,
              fontFamily: 'Nunito, sans-serif',
              pointerEvents: 'none'
            }}
          >
            +{overflow}
          </div>
        )}
      </div>
    );
  };

  // Concurrent-start guard for the verification check, per dashboard
  // instance. Dev StrictMode double-invokes the mount effect, which the
  // 2026-09-29 wake-fix trace caught as TWO verification-check retry
  // sequences 13ms apart. Production doesn't double-invoke, but the guard
  // makes a duplicate structurally impossible on any build. It dedupes only
  // overlapping starts for the same uid; once a run settles, the next
  // mount/refresh/uid-change runs normally (see singleFlight.js).
  const verificationFlightRef = useRef(null);
  if (!verificationFlightRef.current) {
    verificationFlightRef.current = createSingleFlight();
  }

  // Phase 2c: per-section failed states for the three user-facing dashboard
  // loaders (Wantlist Matches, Match Opportunities, Just Listed). Failed is
  // set only when a loader's retry ALSO failed while still current; each
  // loader clears its own flag on start. dashLoadGenRef supersedes in-flight
  // loads on user change/re-mount so a stale resolve (or stale failure)
  // never writes over the current session's sections.
  const [wlmLoadFailed, setWlmLoadFailed] = useState(false);
  const [moLoadFailed, setMoLoadFailed] = useState(false);
  // Slow-but-progressing (not failed) states for the two sections.
  const [wlmLoadSlow, setWlmLoadSlow] = useState(false);
  const [moLoadSlow, setMoLoadSlow] = useState(false);
  const [jlLoadFailed, setJlLoadFailed] = useState(false);
  const dashLoadGenRef = useRef(0);
  // Synchronous per-section in-flight guard (review R6): a Try-again click
  // racing a reconnect-recovery run (or a same-tick double-click) must not
  // start two concurrent copies of one section's loader. Keys include the
  // generation, so a new mount/user's load is never blocked by a stale
  // in-flight run; the ref dies with the instance.
  const sectionFlightRef = useRef(null);
  if (!sectionFlightRef.current) {
    sectionFlightRef.current = createSingleFlight();
  }

  useEffect(() => {
    // Nothing starts until auth is known (src/utils/authResolution.js): the
    // signed-in loaders need the uid, and the public loaders would otherwise
    // run once for the unresolved null and again when the user arrives.
    if (!authResolved) return undefined;
    if (user) perfMark('pxc:dashboard-requests-start');
    // Supersede any in-flight section loads from a previous mount/user: their
    // late resolves (and late failures) compare against this generation and
    // discard themselves. (Phase 2c)
    dashLoadGenRef.current += 1;

    const checkVerification = async () => {
      const user = auth.currentUser;
      if (!user) return;
      // NOTE deliberately no cancellation token here: under dev StrictMode
      // the first (still in-flight) run is the ONLY run — the double-invoked
      // second start is dropped by the guard — so its result must still
      // apply. After a real unmount, the setState is a React 18 no-op.
      // Stale-result safety across a UID change lives in runVerificationCheck
      // (it discards any result whose uid is no longer signed in); offline
      // retry behavior is documented there too.
      await verificationFlightRef.current(user.uid, async () => {
        try {
          await runVerificationCheck(user, setIsVerified);
        } catch (err) {
          console.warn('Verification check skipped (network unavailable):', err?.code || err);
        }
      });
    };
    
    // Cancellation token for loaders that should bail if `user` changes (or
    // the component unmounts) before their async work resolves. Currently
    // only wired into loadPotentialMatchUserIds — see that loader's doc
    // comment for why a stale resolve here is particularly bad (it would
    // flag listings as bidirectional matches for the wrong user).
    const token = { cancelled: false };

    // Only check verification if user is authenticated
    if (user) {
      // ONE wantsMyPedals read for the whole mount, shared by the three
      // consumers that each used to run this identical query themselves
      // (recommendations, potential-match banner set, MO counter). The
      // .catch → null keeps a shared-read failure from rejecting unhandled;
      // each consumer treats null as "run your own query" (the pre-sharing
      // behavior), so the failure mode is at worst what shipped before.
      const sharedWmpRowsPromise = fetchWantsMyPedalsRows(user.uid).catch((err) => {
        console.error('Error fetching shared wantsMyPedals rows:', err);
        return null;
      });
      checkVerification();
      checkUserExperience();
      loadRecommendedPedals(sharedWmpRowsPromise);
      loadWantListMatches();
      startMatchCounters(token);
      loadPotentialMatchUserIds(token, sharedWmpRowsPromise);
    } else {
      setIsExperiencedUser(false);
      setRecommendedPedals([]);
      writeRecommendedCache(null, []);
      setIsLoadingRecommended(false);
      setWantListMatches([]);
      setIsLoadingWantListMatches(false);
      setNewWantListMatchCount(0);
      setNewMatchOpportunitiesCount(0);
      setPotentialMatchUserIds(new Set());
      // R3: stale failed states must not survive sign-out — a consumed
      // one-shot recovery can otherwise strand them (the loaders early-return
      // on !user without clearing, and the recovery effect never re-arms
      // because the flags never change).
      setWlmLoadFailed(false);
      setMoLoadFailed(false);
      setJlLoadFailed(false);
    }
    
    // Always load featured pedals and successful trades, regardless of authentication status
    loadFeaturedPedals();
    loadSuccessfulTrades();

    return () => {
      token.cancelled = true;
      // Account change / unmount: drop the counter subscription so no late
      // result from the old user's computation can land on the new user.
      if (matchCountersUnsubRef.current) {
        matchCountersUnsubRef.current();
        matchCountersUnsubRef.current = null;
      }
    };
  }, [user, authResolved]);

  // One-shot reconnect recovery for FAILED dashboard sections only (Phase
  // 2c, same tested mechanism as Browse/PedalDetails): the first
  // online/tab-visible signal re-runs ONLY the sections whose loads failed;
  // sections that loaded (or are legitimately empty, server-confirmed) are
  // untouched. One-shot arm means overlapping signals can't stack loads; a
  // recovery run that fails again re-arms a fresh one-shot; disarms on
  // unmount and whenever no section is failed. Each section's Try-again
  // button re-runs just its own loader.
  useEffect(() => {
    if (!wlmLoadFailed && !moLoadFailed && !jlLoadFailed) return undefined;
    return armRecoveryOnReconnect(() => {
      if (wlmLoadFailed) loadWantListMatches();
      if (moLoadFailed) loadRecommendedPedals(null);
      if (jlLoadFailed) loadFeaturedPedals();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wlmLoadFailed, moLoadFailed, jlLoadFailed]);

  // Live-update the match counter badges via a Firestore listener on
  // `wantsMyPedals`. Without this, the badges only refresh on dashboard
  // mount — meaning a user sitting on the dashboard wouldn't see a new
  // match arrive in realtime even though the bell notification would.
  //
  // The listener starts only AFTER startMatchCounters has claimed the
  // first-visit cursor; a listener firing before that claim could read a
  // pre-claim cursor of `null`, count every existing wmp doc as new, and
  // briefly flash a "9+ new" badge before the claim resolves. Sequencing it
  // behind the claim (instead of the old 1.5 s timer) removes both the
  // flash risk and the second full computation.
  //
  // Native resume (2026-10-31): App.js rebuilds the Firestore connection and
  // then announces it; the listeners (strip, counters) re-sync on their own,
  // the Trade Opportunities cards are a one-shot read, so refresh them here.
  useEffect(() => {
    if (!user) return undefined;
    const onResumed = () => { loadRecommendedPedals(null); };
    window.addEventListener('pxc:resumed', onResumed);
    return () => window.removeEventListener('pxc:resumed', onResumed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Counter subscription: started by startMatchCounters from the [user]
  // effect, after the first-visit cursor claim (Stage 2, 2026-10-31). The
  // previous 1.5 s timer-started subscription recomputed both counters a
  // second time from its initial snapshots.

  // When true, the carousel inner's transform jumps without animation.
  // Used during the wraparound from the last slide back to slide 0 so the carousel
  // doesn't visibly sweep backwards through every prior slide over 0.8s — which
  // both looks bad and accumulates GPU compositing artifacts (the thin sliver
  // appearing at the right edge after several cycles).
  const [carouselSnapping, setCarouselSnapping] = useState(false);

  // Set up carousel rotation for successful trades (10 seconds)
  useEffect(() => {
    if (successfulTrades.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentTradeIndex((prevIndex) => {
        const next = (prevIndex + 1) % successfulTrades.length;
        // Wrapping from last slide back to slide 0 — snap instantly instead of animating
        // a 0.8s reverse-sweep through every prior slide.
        if (next === 0 && prevIndex !== 0 && successfulTrades.length > 1) {
          setCarouselSnapping(true);
          // Two requestAnimationFrames: first frame React commits the transform with
          // transition:'none' applied, second frame we re-enable transition for the
          // next normal slide-to-slide auto-advance. Using a single rAF can race the
          // commit and re-enable transition before the snap actually paints.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setCarouselSnapping(false));
          });
        }
        return next;
      });
    }, 6800); // 6 seconds visible + 0.8s transition
    
    return () => clearInterval(interval);
  }, [successfulTrades]);

  // Measure the Recent Trades carousel viewport in integer pixels so the slide transform
  // can advance by an exact pixel multiple (instead of a percentage that may compound
  // sub-pixel rounding errors over many cycles and leak the next slide's left edge in).
  // Re-runs whenever the carousel mounts/unmounts (driven by successfulTrades.length flipping
  // between 0 and >0). The ResizeObserver also keeps the value fresh on viewport resize.
  useEffect(() => {
    const wrapper = recentTradesCarouselRef.current;
    if (!wrapper) return;

    const measure = () => {
      const w = Math.round(wrapper.getBoundingClientRect().width);
      setRecentTradesSlideWidth((prev) => (prev !== w ? w : prev));
    };

    measure();

    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(wrapper);
    } else {
      // Fallback for older browsers: re-measure on window resize only.
      window.addEventListener('resize', measure);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener('resize', measure);
      }
    };
  }, [successfulTrades.length]);



  const checkUserExperience = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setIsExperiencedUser(false);
        return;
      }

      // Check if user has added at least one pedal
      const q = query(
        collection(db, 'listings'),
        where('ownerId', '==', user.uid),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      setIsExperiencedUser(snapshot.size > 0);
    } catch (error) {
      console.error('Error checking user experience:', error);
      setIsExperiencedUser(false);
    }
  };

  const loadRecommendedPedals = async (sharedWmpRowsPromise) => {
    const user = auth.currentUser;
    if (!user) {
      // R1: the removed try/finally used to clear loading even on this early
      // return. Without these, an auth-gap mount (realistically: sign-out
      // mid-session — the SDK sets currentUser BEFORE notifying listeners,
      // so a truthy `user` prop implies currentUser on mount) left the
      // skeleton up forever and made Try-again a dead button. The legit
      // load still runs when the `user` prop next changes.
      writeRecommendedCache(null, []);
      setRecommendedPedals([]);
      setIsLoadingRecommended(false);
      setMoLoadFailed(false);
      return;
    }
    const myGen = dashLoadGenRef.current;
    await sectionFlightRef.current(`mo:${myGen}`, async () => {
    setMoLoadFailed(false);
    // Keep showing the cards we already have (from this mount or the cache)
    // while the refresh runs; the skeleton only when there is nothing to show.
    // An account change without a remount must not keep the previous
    // account's cards in state: the cache is per uid, so an empty read here
    // means "nothing of yours yet" and the state is cleared to match.
    const cachedForUser = readRecommendedCache(user.uid);
    if (cachedForUser.length === 0) setRecommendedPedals([]);
    setIsLoadingRecommended(cachedForUser.length === 0);
    // Timeout + one retry with an honest outcome (Phase 2c — same tested
    // flow as Browse). An offline false-empty from the wantsMyPedals query
    // now throws (see recommendations.js) instead of rendering the
    // legitimate "No recommendations yet" copy.
    setMoLoadSlow(false);
    const outcome = await runWithSoftTimeout(async () => {
      // Reuse the dashboard's single wantsMyPedals read when available
      // (null on shared-read failure → the loader runs its own query,
      // exactly the pre-sharing behavior).
      const wmpRows = sharedWmpRowsPromise ? await sharedWmpRowsPromise : null;
      const pedals = await getPedalsFromUsersWhoWantMine(user.uid, 8, { wmpRows });
      const enrichedPedals = await enrichPedalsWithOwnerData(pedals);
      if (dashLoadGenRef.current === myGen) perfMark('pxc:trade-opportunities-data');
      if (dashLoadGenRef.current === myGen) setRecommendedPedals(enrichedPedals);
      if (dashLoadGenRef.current === myGen) writeRecommendedCache(user.uid, enrichedPedals);
      if (dashLoadGenRef.current === myGen) {
        // A result that lands after a hard timeout still counts: clear the error.
        setMoLoadFailed(false);
        setMoLoadSlow(false);
        setIsLoadingRecommended(false);
      }
    }, {
      softMs: DASH_SLOW_MS,
      hardMs: DASH_HARD_MS,
      retryDelayMs: DASH_RETRY_DELAY_MS,
      isCurrent: () => dashLoadGenRef.current === myGen,
      label: 'dash:recommended',
      onSlow: () => setMoLoadSlow(true)
    });
    if (dashLoadGenRef.current === myGen) {
      if (outcome === 'failed') setMoLoadFailed(true);
      setMoLoadSlow(false);
      setIsLoadingRecommended(false);
    }
    });
  };

  const loadWantListMatches = async () => {
    const user = auth.currentUser;
    if (!user) {
      // R1: see loadRecommendedPedals — clear loading/failed on the early
      // return, exactly as the removed finally used to.
      setWantListMatches([]);
      setIsLoadingWantListMatches(false);
      setWlmLoadFailed(false);
      return;
    }
    const myGen = dashLoadGenRef.current;
    await sectionFlightRef.current(`wlm:${myGen}`, async () => {
    setWlmLoadFailed(false);
    setWlmLoadSlow(false);
    setIsLoadingWantListMatches(true);
    // One attempt; "still loading" after DASH_SLOW_MS; terminal failure only
    // at DASH_HARD_MS or a real rejection (retried once). An offline
    // false-empty from the wants/wmp queries throws (see wantListMatches.js).
    const outcome = await runWithSoftTimeout(async () => {
      // Get up to 8 pedals matching user's Wantlist — served from the
      // server-maintained wantsMyPedals rows (O(my matches) reads) instead of
      // scanning every active listing (TECH_DEBT.md R3).
      const matches = await getWantListMatchesFromServer(user.uid, 8);
      if (dashLoadGenRef.current === myGen) setWantListMatches(matches);
      if (dashLoadGenRef.current === myGen) {
        // A result that lands after a hard timeout still counts: clear the error.
        setWlmLoadFailed(false);
        setWlmLoadSlow(false);
        setIsLoadingWantListMatches(false);
      }
    }, {
      softMs: DASH_SLOW_MS,
      hardMs: DASH_HARD_MS,
      retryDelayMs: DASH_RETRY_DELAY_MS,
      isCurrent: () => dashLoadGenRef.current === myGen,
      label: 'dash:wantListMatches',
      onSlow: () => setWlmLoadSlow(true)
    });
    if (dashLoadGenRef.current === myGen) {
      if (outcome === 'failed') setWlmLoadFailed(true);
      setWlmLoadSlow(false);
      setIsLoadingWantListMatches(false);
    }
    });
  };

  // Load the set of users who currently want one of MY pedals. Drives the
  // "Potential Match" banner on the Wantlist Matches carousel — flagged when
  // the listing's owner is in this set (i.e., they want one of OUR pedals,
  // making the match bidirectional). One read per dashboard mount; same query
  // shape as the page-level loaders in WantListMatchesPage / LatestPedals.
  //
  // Accepts an optional cancellation token (`{ cancelled: boolean }`) from
  // the calling effect. If the user switches accounts (or unmounts) while
  // the Firestore read is in flight, the token flips to true and we skip
  // the setState — otherwise a stale result for the previous user could
  // overwrite the new user's empty set and incorrectly flag listings as
  // "Potential Match".
  const loadPotentialMatchUserIds = async (token, sharedWmpRowsPromise) => {
    try {
      if (!user) {
        if (!token?.cancelled) setPotentialMatchUserIds(new Set());
        return;
      }
      // Reuse the dashboard's single wantsMyPedals read when available; on
      // shared-read failure (null) fall back to this loader's own identical
      // query, preserving its pre-sharing behavior.
      let rows = sharedWmpRowsPromise ? await sharedWmpRowsPromise : null;
      if (!rows) {
        const wmpSnapshot = await getDocs(
          query(
            collection(db, 'wantsMyPedals'),
            where('pedalOwnerId', '==', user.uid)
          )
        );
        rows = wmpSnapshot.docs.map(d => d.data());
      }
      if (token?.cancelled) return;
      const mySaleOnly = await getMySaleOnlyPedalIds(user.uid); // plan §5
      if (token?.cancelled) return;
      const userIds = new Set();
      rows.forEach(r => { if (!mySaleOnly.has(r.pedalId)) userIds.add(r.wantingUserId); });
      setPotentialMatchUserIds(userIds);
    } catch (error) {
      if (token?.cancelled) return;
      console.error('Error loading potential match user ids:', error);
      setPotentialMatchUserIds(new Set());
    }
  };

  // Load "X new" badge counts for the Wantlist Matches and Match Opportunities
  // carousel headers. Sequenced so the first-visit cursor claim happens BEFORE
  // we count — otherwise a brand-new user would see "9+ new" on every existing
  // match the very first time they hit the dashboard. See
  // src/utils/dashboardMatchCounters.js for full semantics.
  //
  // Failures here are silent (counts default to 0) — the badge is a hint, not
  // a critical surface, and the matchmaker / dashboard data flow are unaffected.
  const startMatchCounters = async (token) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const { wlmInitialized, moInitialized } = await markFirstVisitIfNeeded(user.uid);
      if (token.cancelled) return;

      // First-visit shortcut: if BOTH cursors were just claimed, both counts
      // are necessarily 0 on this pass; the subscription still starts so a
      // match that lands later is counted.
      if (wlmInitialized && moInitialized) {
        setNewWantListMatchCount(0);
        setNewMatchOpportunitiesCount(0);
      }

      // The subscription owns the computation: one pass once both listeners
      // have their initial snapshot, then one debounced pass per change.
      if (matchCountersUnsubRef.current) matchCountersUnsubRef.current();
      // Trade Opportunities cards refresh on the same signal as their badge:
      // every wantsMyPedals change after the first pass (someone new wants
      // one of my pedals, a row removed) re-runs the card loader, so the
      // cards stay current while the dashboard is open without a reload.
      // The first callback coincides with the mount load and is skipped.
      let firstCounterPass = true;
      matchCountersUnsubRef.current = subscribeToMatchCounters(
        user.uid,
        ({ wlmCount, moCount }) => {
          if (token.cancelled) return;
          setNewWantListMatchCount(wlmCount);
          setNewMatchOpportunitiesCount(moCount);
          if (firstCounterPass) { firstCounterPass = false; return; }
          loadRecommendedPedals(null);
        },
        { firstPass: { wlmInitialized, moInitialized } }
      );
    } catch (err) {
      console.error('❌ Error starting match counters:', err);
      setNewWantListMatchCount(0);
      setNewMatchOpportunitiesCount(0);
    }
  };

  // Stale-while-revalidate cache for the 5-star carousel.
  // - Within TTL: render cached, no refetch.
  // - Beyond TTL: render cached instantly + silently refetch in background.
  // - Cache lives in sessionStorage (per-tab; cleared on tab close).
  // Bumped to v2 when admin-completed trades started being filtered out — a
  // stale v1 payload would keep showing an excluded trade for the whole TTL.
  const CAROUSEL_CACHE_KEY = 'pxc_dashboard_5star_carousel_v3'; // v3: items built from publicTrades (2026-10-30)
  const CAROUSEL_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  const readCarouselCache = () => {
    try {
      const raw = sessionStorage.getItem(CAROUSEL_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.data) || typeof parsed.cachedAt !== 'number') {
        return null;
      }
      const age = Date.now() - parsed.cachedAt;
      return { data: parsed.data, age, expired: age > CAROUSEL_CACHE_TTL_MS };
    } catch (err) {
      return null;
    }
  };

  const writeCarouselCache = (data) => {
    try {
      sessionStorage.setItem(
        CAROUSEL_CACHE_KEY,
        JSON.stringify({ data, cachedAt: Date.now() })
      );
    } catch (err) {
      // sessionStorage can throw (quota, private mode); cache is best-effort
    }
  };

  const fetchSuccessfulTradesFromFirestore = async () => {
    // Order by completedAt DESC so the carousel always reflects the MOST RECENT
    // completed trades (this is a "Recent Trades" showcase). Without server-side
    // ordering, Firestore returns an arbitrary sample and genuinely recent trades
    // can fall outside it. Every completed trade since the completion flow shipped
    // sets completedAt, so ordering on it surfaces new trades correctly; a handful
    // of pre-completedAt legacy trades are intentionally excluded from "recent".
    //
    // Reads publicTrades, not tradeOffers (2026-10-30). This carousel is shown
    // to every visitor, signed in or not, and until then it downloaded the
    // newest 100 whole trade documents, each carrying both parties' contact,
    // address, payment and shipping details it never displayed (15 MB of
    // stream content per dashboard load, and a privacy exposure). publicTrades
    // is the server-maintained allowlisted mirror (functions/publicTradesShape.js):
    // uids, pedal make/model/one image, ratings and comments. Same ordering,
    // same limit, same client-side selection below, so the showcase content is
    // unchanged. Requires the publicTrades (kind ASC, completedAt DESC) index.
    const tradesQuery = query(
      collection(db, 'publicTrades'),
      where('kind', '==', 'trade'),
      orderBy('completedAt', 'desc'),
      limit(100)
    );

    const tradesSnapshot = await getDocs(tradesQuery);

    const userIds = new Set();
    const tradesData = [];

    for (const tradeDoc of tradesSnapshot.docs) {
      const trade = tradeDoc.data();

      // Skip trades without pedal snapshots (older trades before snapshot feature).
      // Multi-pedal trades may have ONLY pedalSnapshots.initiatorPedals (plural array)
      // and not the legacy singular field — accept either.
      const hasInitiator =
        (Array.isArray(trade.pedalSnapshots?.initiatorPedals) && trade.pedalSnapshots.initiatorPedals.length > 0) ||
        !!trade.pedalSnapshots?.initiatorPedal;
      const hasResponder = !!trade.pedalSnapshots?.responderPedal;

      if (!hasInitiator || !hasResponder) {
        continue;
      }

      // Admin-completed trades are excluded from the showcase. These are trades
      // the normal delivery path could never finish — a package lost in transit,
      // typically settled with a manual refund — so however sincere the feedback,
      // presenting them as recent successes misrepresents what happened. The
      // trade is still complete everywhere else: both users can rate each other
      // and it still counts on their profiles.
      if (trade.showcaseEligible === false) {
        continue;
      }

      tradesData.push({ id: trade.sourceId || tradeDoc.id, ...trade });

      if (trade.fromUserFeedback?.rating === 5) {
        userIds.add(trade.fromUserId);
      }
      if (trade.toUserFeedback?.rating === 5) {
        userIds.add(trade.toUserId);
      }
    }

    // One batched read per 30 raters instead of one getDoc per rater (66 observed
    // on a production load). Selection contract kept: a 5-star review whose
    // reviewer has no publicProfiles doc is not shown, so a missing profile
    // resolves to a sentinel and is dropped, exactly as the per-rater
    // `exists()` check did.
    const MISSING_PROFILE = { __missing: true };
    const fetchedProfiles = await fetchPublicProfiles(Array.from(userIds), { fallback: MISSING_PROFILE });
    const userProfiles = {};
    for (const [userId, profile] of Object.entries(fetchedProfiles)) {
      if (profile && !profile.__missing) userProfiles[userId] = profile;
    }

    const fiveStarTrades = [];

    // Trade data model: multi-pedal trades store the initiator side as `initiatorPedals` (plural array);
    // legacy/single-pedal trades use `initiatorPedal` (singular). Responder is always single
    // (per the multi-for-one trade rule). Normalize both shapes to an array.
    const getInitiatorPedalsArray = (snapshots) => {
      if (Array.isArray(snapshots?.initiatorPedals) && snapshots.initiatorPedals.length > 0) {
        return snapshots.initiatorPedals;
      }
      return snapshots?.initiatorPedal ? [snapshots.initiatorPedal] : [];
    };

    for (const trade of tradesData) {
      const perspectives = [];
      const initiatorPedalsArr = getInitiatorPedalsArray(trade.pedalSnapshots);
      const responderPedalObj = trade.pedalSnapshots?.responderPedal;

      if (trade.fromUserFeedback?.rating === 5 && userProfiles[trade.fromUserId]) {
        // From-user (initiator) RECEIVED the responder's single pedal and GAVE their own (potentially multiple)
        const receivedPedals = responderPedalObj ? [responderPedalObj] : [];
        const givenPedals = initiatorPedalsArr;

        // Require front pedal of each side to have an image (back-of-stack pedals without images are gracefully skipped at render)
        if (receivedPedals[0] && givenPedals[0]) {
          perspectives.push({
            id: `${trade.id}_from`,
            ...trade,
            reviewerUserId: trade.fromUserId,
            reviewerProfile: userProfiles[trade.fromUserId],
            reviewerFeedback: trade.fromUserFeedback,
            pedalsReceived: receivedPedals,
            pedalsGiven: givenPedals
          });
        }
      }

      if (trade.toUserFeedback?.rating === 5 && userProfiles[trade.toUserId]) {
        // To-user (responder) RECEIVED the initiator's pedals (potentially multiple) and GAVE their own single pedal
        const receivedPedals = initiatorPedalsArr;
        const givenPedals = responderPedalObj ? [responderPedalObj] : [];

        if (receivedPedals[0] && givenPedals[0]) {
          perspectives.push({
            id: `${trade.id}_to`,
            ...trade,
            reviewerUserId: trade.toUserId,
            reviewerProfile: userProfiles[trade.toUserId],
            reviewerFeedback: trade.toUserFeedback,
            pedalsReceived: receivedPedals,
            pedalsGiven: givenPedals
          });
        }
      }

      // If both perspectives are valid, randomly choose only ONE to avoid sequential duplicates
      if (perspectives.length > 0) {
        const chosenPerspective = perspectives[Math.floor(Math.random() * perspectives.length)];
        fiveStarTrades.push(chosenPerspective);
      }
    }

    // Sort client-side: prefer completedAt, fall back to feedback timestamps.
    const sorted = [...fiveStarTrades].sort((a, b) => {
      let aTime = a.completedAt?.toDate?.()?.getTime() || 0;
      let bTime = b.completedAt?.toDate?.()?.getTime() || 0;

      if (!aTime) {
        // Feedback timestamps are stored as `submittedAt` (not `timestamp`).
        const aFromTime = a.fromUserFeedback?.submittedAt?.toDate?.()?.getTime() || 0;
        const aToTime = a.toUserFeedback?.submittedAt?.toDate?.()?.getTime() || 0;
        aTime = Math.max(aFromTime, aToTime);
      }
      if (!bTime) {
        const bFromTime = b.fromUserFeedback?.submittedAt?.toDate?.()?.getTime() || 0;
        const bToTime = b.toUserFeedback?.submittedAt?.toDate?.()?.getTime() || 0;
        bTime = Math.max(bFromTime, bToTime);
      }

      if (!aTime && !bTime) return 0;
      if (!aTime) return 1;
      if (!bTime) return -1;

      return bTime - aTime;
    });

    return sorted;
  };

  const loadSuccessfulTrades = async ({ forceRefresh = false } = {}) => {
    // Pull-to-refresh (and other explicit refreshes) bypass the cache entirely so
    // a freshly completed trade shows up immediately instead of waiting out the TTL.
    if (forceRefresh) {
      try {
        const fresh = await fetchSuccessfulTradesFromFirestore();
        writeCarouselCache(fresh);
        setSuccessfulTrades(fresh);
      } catch (error) {
        console.error('Error force-refreshing successful trades:', error);
      } finally {
        setIsLoadingTrades(false);
      }
      return;
    }

    const cached = readCarouselCache();

    // Fresh cache: render and skip refetch entirely
    if (cached && !cached.expired) {
      setSuccessfulTrades(cached.data);
      setIsLoadingTrades(false);
      return;
    }

    // Stale cache: render immediately, refetch in background
    if (cached) {
      setSuccessfulTrades(cached.data);
      setIsLoadingTrades(false);
      try {
        const fresh = await fetchSuccessfulTradesFromFirestore();
        writeCarouselCache(fresh);
        setSuccessfulTrades(fresh);
      } catch (error) {
        console.error('Error refreshing successful trades in background:', error);
      }
      return;
    }

    // No cache: full load with loading state
    try {
      setIsLoadingTrades(true);
      const fresh = await fetchSuccessfulTradesFromFirestore();
      writeCarouselCache(fresh);
      setSuccessfulTrades(fresh);
    } catch (error) {
      console.error('Error loading successful trades:', error);
      setSuccessfulTrades([]);
    } finally {
      setIsLoadingTrades(false);
    }
  };

  const loadFeaturedPedals = async () => {
    const myGen = dashLoadGenRef.current;
    // V2: same generation-keyed single-flight as the other two failed-band
    // sections — this loader also has a Try-again button and reconnect
    // recovery, so overlapping starts were possible here too.
    await sectionFlightRef.current(`jl:${myGen}`, async () => {
    setJlLoadFailed(false);
    setIsLoadingFeatured(true);
    // Timeout + one retry with an honest outcome (Phase 2c): the user's own
    // screenshots showed this section claiming an empty marketplace after an
    // offline blip, because the query resolves cache-empty instead of
    // rejecting.
    const outcome = await runWithTimeoutAndRetry(async () => {
      // 🔄 REACTIVATION FIX: Fetch more pedals to sort by effective date (createdAt or reactivatedAt)
      // This ensures reactivated pedals appear in "Latest Pedals" section
      const q = query(
        collection(db, 'listings'),
        where('active', '==', true),
        orderBy('listedAt', 'desc'), // Ensure we get the most recent pedals first
        limit(50) // Fetch more to account for filtering and custom sorting
      );
      const snapshot = await getDocs(q);
      throwIfOfflineEmpty(snapshot, 'dash:justListed');

      // Collect pedals and unique owner IDs
      const pedals = [];
      const ownerIds = new Set();
      
      
      for (const docSnap of snapshot.docs) {
        const pedalData = docSnap.data();
        
        // Skip pedals that are currently in trades
        if (pedalData.inTrade) {
          continue;
        }
        
        // 🔄 NEW REACTIVATION LOGIC: Skip pedals hidden from pedalboards 
        if (pedalData.hiddenFromPedalboard) {
          continue;
        }
        
        // Sort key: listedAt → reactivatedAt → createdAt
        const effectiveDate = listingListedAtMs(pedalData);
        
        pedals.push({
          id: docSnap.id,
          ...pedalData,
          effectiveDate
        });
        
        ownerIds.add(pedalData.ownerId);
      }

      // Sort by listedAt priority (newest first), take the 8 shown, and only
      // THEN fetch owner profiles — the fetch used to run for owners of all
      // ~50 fetched listings, discarding up to 42 round trips per dashboard
      // load. Batched via documentId()-in: one round trip for all 8. The
      // `user` gate is preserved from the code this replaced (logged-out
      // dashboards show the 'User' fallback, as before).
      pedals.sort((a, b) => b.effectiveDate - a.effectiveDate);
      const shown = pedals.slice(0, 8);

      const shownOwnerIds = Array.from(new Set(shown.map(p => p.ownerId).filter(Boolean)));
      const userData = user && shownOwnerIds.length > 0
        ? await fetchPublicProfiles(shownOwnerIds)
        : {};

      const latestPedals = shown.map(pedal => ({
        ...pedal,
        owner: {
          username: (userData[pedal.ownerId] || {})?.username || 'User',
          badge: getTradeBadge((userData[pedal.ownerId] || {})?.completedTrades || 0),
          lastActiveWeek: (userData[pedal.ownerId] || {})?.lastActiveWeek || null,
          lastActiveAt: (userData[pedal.ownerId] || {})?.lastActiveAt || null,
          responderBadge: (userData[pedal.ownerId] || {})?.responderBadge || null
        }
      }));
      if (dashLoadGenRef.current === myGen) setFeaturedPedals(latestPedals);
    }, {
      timeoutMs: DASH_FETCH_TIMEOUT_MS,
      retryDelayMs: DASH_RETRY_DELAY_MS,
      isCurrent: () => dashLoadGenRef.current === myGen,
      label: 'dash:justListed'
    });
    if (dashLoadGenRef.current === myGen) {
      if (outcome === 'failed') setJlLoadFailed(true);
      setIsLoadingFeatured(false);
    }
    });
  };

  const refreshDashboardRef = useRef(null);
  refreshDashboardRef.current = async () => {
    // V3: an explicit pull-to-refresh must never be silently dropped because
    // a mount load is still in flight under the same single-flight key.
    // Bumping the generation gives this refresh fresh keys (so it actually
    // runs) and generation-discards every write — content, failed flag,
    // loading flag — from any still-running older load. NOTE the honest
    // semantics: the single-flight guard is PER-GENERATION, so an old
    // in-flight request may briefly overlap the new one; the old run's
    // writes are all generation-guarded no-ops, which is the designed
    // supersede behavior (same model as Browse's fetch token), not
    // cross-generation serialization.
    dashLoadGenRef.current += 1;
    await Promise.all([
      loadFeaturedPedals(),
      loadSuccessfulTrades({ forceRefresh: true }),
    ]);
    if (user) {
      // Fresh shared read per refresh — never reuse the mount's rows.
      const sharedWmpRowsPromise = fetchWantsMyPedalsRows(user.uid).catch((err) => {
        console.error('Error fetching shared wantsMyPedals rows:', err);
        return null;
      });
      await Promise.all([
        loadRecommendedPedals(sharedWmpRowsPromise),
        loadWantListMatches(),
        // Counters: the live subscription already holds the current rows;
        // ask it for one fresh pass (cursors, listings, wants re-read).
        matchCountersUnsubRef.current ? matchCountersUnsubRef.current.recompute() : Promise.resolve(),
        loadPotentialMatchUserIds({ cancelled: false }, sharedWmpRowsPromise),
      ]);
    }
  };

  const refreshDashboard = useCallback(async () => {
    await refreshDashboardRef.current?.();
  }, [user]);

  useRegisterPullToRefresh(refreshDashboard);

  const getTradeBadge = (count) => {
    if (count === undefined || count === null || isNaN(count)) return 0;
    return count; // Show exact number of completed trades, including 0
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  // Define "Wantlist Matches" section - Only show for logged-in users
  const wantListMatchesSection = user && (
    <>
      <style>{`
        @media (max-width: 768px) {
          .dashboard-carousel-card {
            zoom: 0.75;
          }
        }
      `}</style>
      {/* Subtle Section Divider — same spacing as Latest Pedals (no extra top cushion) */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
        <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '1rem 0' }}></div>
      </div>
      
      <style>{`
        @media (min-width: 769px) {
          .want-list-section-logged-in {
            padding-top: 0.25rem !important;
          }
        }
      `}</style>
      <div className="mx-auto want-list-section-logged-in" style={{ paddingTop: '0.5rem', paddingBottom: '1rem', paddingLeft: '1.5rem', paddingRight: '1.5rem', backgroundColor: '#F7F3EB' }}>
        <div style={{ 
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
        {/* Section Header */}
        <div id="want-list-matches-header" className="dashboard-carousel-header-block">
          <div className="dashboard-carousel-eyebrow-row">
            <span className="dashboard-carousel-eyebrow dashboard-carousel-eyebrow--want-list-matches">Matching your wants</span>
            {(isLoadingWantListMatches || wantListMatches.length > 0) && (
              <Link to="/want-list-matches" className="dashboard-carousel-see-all">
                See all →
              </Link>
            )}
          </div>
          <h2 className="dashboard-carousel-section-title-heading">
            <Link
              to="/want-list-matches"
              className="dashboard-carousel-section-title dashboard-carousel-section-title-link"
            >
              Saved Pedals
            </Link>
          </h2>
          {/* The count arrives after first paint. The subtitle is positioned
              out of flow (dashboardSectionHeaders.css) inside the gap that
              already exists under the title, so it neither reserves a blank
              line nor pushes the cards down when it appears. */}
          {newWantListMatchCount > 0 && (
            <span className="dashboard-carousel-subtitle">
              {formatBadgeCount(newWantListMatchCount)} new since last visit
            </span>
          )}
        </div>
      
      {/* Loading state */}
      {isLoadingWantListMatches ? (
        <div className="relative group p-4 flex justify-center" style={{ minHeight: '400px' }}>
          {wlmLoadSlow && (
            <p style={{ position: 'absolute', top: '0.5rem', left: 0, right: 0, textAlign: 'center', margin: 0, color: '#6b7280', fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem' }}>Still loading on a slow connection…</p>
          )}
          <div className="flex justify-center overflow-x-auto pb-4 min-h-[200px] w-full want-list-scroll-mobile"
            style={{ 
              paddingLeft: '0rem',
              marginLeft: '-6rem',
              paddingRight: '0rem',
              marginRight: '-6rem',
              overflowX: 'auto',
              overflowY: 'hidden'
            }}
          >
            <div className="dashboard-carousel-scroll-track shrink-0 self-start">
            <div className="flex gap-6" style={{ display: 'flex', gap: '32px', alignItems: 'flex-start', justifyContent: 'center' }}>
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="dashboard-carousel-card" style={{ width: '331px' }}>
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
                    <div className="w-full h-36 bg-gray-200"></div>
                    <div className="p-3">
                      <div className="h-3 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="flex justify-between">
                        <div className="h-4 bg-gray-200 rounded w-12"></div>
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>
      ) : wlmLoadFailed && wantListMatches.length === 0 ? (
        /* FAILED load ≠ no matches (Phase 2c): the empty copy below stays
           reserved for server-confirmed results. The length gate (review
           B3) keeps a late/refresh failure from replacing loaded cards —
           same contract as Browse. */
        <SectionLoadFailed />
      ) : wantListMatches.length === 0 ? (
        <div className="bg-gray-50 rounded-lg text-center" style={{ padding: '0.25rem 2rem 1rem 2rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <p className="text-gray-500 text-lg" style={{ marginBottom: '0.5rem' }}>No matches yet</p>
          <p className="text-gray-400 text-sm" style={{ margin: 0 }}>
            When a pedal from your Wantlist gets listed, it will appear here.
          </p>
        </div>
      ) : (
        <div className="relative group p-4 flex justify-center want-list-pedals-container" style={{ minHeight: '400px' }}>
          {wlmLoadFailed && wantListMatches.length > 0 && (
            <div role="status" style={{ margin: '0 0 0.75rem 0', padding: '0.5rem 0.9rem', borderRadius: '10px', background: '#FEF3C7', color: '#92400E', fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span>Couldn&rsquo;t refresh these. Showing what loaded earlier.</span>
              <button type="button" onClick={() => loadWantListMatches()} style={{ background: 'none', border: 'none', color: '#5A9ECB', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0 }}>Try again</button>
            </div>
          )}
          <style>{`
            @media (max-width: 768px) {
              .want-list-inner-mobile {
                margin: 0 !important;
              }
            }
            
            /* Custom scrollbar for Wantlist Matches */
            #want-list-pedals-scroll::-webkit-scrollbar {
              height: 10px;
            }
            
            #want-list-pedals-scroll::-webkit-scrollbar-track {
              background: linear-gradient(to bottom, transparent 4px, #6B7280 4px, #6B7280 6px, transparent 6px);
            }
            
            #want-list-pedals-scroll::-webkit-scrollbar-thumb {
              background: #C9A227;
              border-radius: 5px;
              border: 2px solid #F7F3EB;
            }
            
            #want-list-pedals-scroll::-webkit-scrollbar-thumb:hover {
              background: #b8911f;
            }
          `}</style>
          <div
            id="want-list-pedals-scroll"
            ref={wantListScrollContainerRef}
            className="flex overflow-x-auto w-full want-list-scroll-mobile"
            style={{ 
              paddingLeft: '0rem',
              paddingRight: '0rem',
              paddingBottom: '2rem',
              overflowX: 'auto',
              overflowY: 'hidden',
              minHeight: '200px',
              position: 'relative',
              scrollBehavior: 'auto',
              width: '100%',
              maxWidth: '100%'
            }}
          >
            <div className="dashboard-carousel-scroll-track shrink-0 self-start">
            <div
              className="flex gap-6 want-list-inner-mobile"
              style={{ 
                display: 'flex',
                gap: '32px',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                ...(isMobile
                  ? {}
                  : {
                      minWidth: `${wantListMatches.length * 331 + (wantListMatches.length - 1) * 32}px`
                    }),
                width: 'max-content',
                margin: '0'
              }}
            >
              {wantListMatches.map((pedal) => (
                <div key={pedal.id} className="dashboard-carousel-card" style={{ width: '331px' }}>
                  <PedalCard
                    pedal={pedal}
                    currentUser={user}
                    firestoreUser={firestoreUser}
                    variant="want-list-match"
                    isPotentialMatch={!!user && pedal.ownerId !== user.uid && allowsTrade(pedal) && potentialMatchUserIds.has(pedal.ownerId)}
                  />
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
    </>
  );

  // Define "Recommended Pedals" section - Only show for logged-in users
  const recommendedPedalsSection = user && (
    <>
      {/* Subtle Section Divider — same as Wantlist / Latest Pedals */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
        <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '1rem 0' }}></div>
      </div>
      
      <style>{`
        @media (min-width: 769px) {
          .recommendations-section-logged-in {
            padding-top: 0.25rem !important;
          }
        }
      `}</style>
      <div className="mx-auto recommendations-section-logged-in section-additional-matches" style={{ paddingTop: '0.5rem', paddingBottom: '1rem', paddingLeft: '1.5rem', paddingRight: '1.5rem', backgroundColor: '#F7F3EB' }}>
        <div style={{ 
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
        {/* Section Header */}
        <div id="match-opportunities-header" className="dashboard-carousel-header-block">
          <div className="dashboard-carousel-eyebrow-row">
            <span className="dashboard-carousel-eyebrow dashboard-carousel-eyebrow--match-opportunities">Instant matches</span>
            {(isLoadingRecommended || recommendedPedals.length > 0) && (
              <Link to="/recommendations" className="dashboard-carousel-see-all">
                See all →
              </Link>
            )}
          </div>
          <h2 className="dashboard-carousel-section-title-heading">
            <Link
              to="/recommendations"
              data-tip-anchor="trade-opportunities"
              className="dashboard-carousel-section-title dashboard-carousel-section-title-link"
            >
              Trade Opportunities
            </Link>
          </h2>
          {/* Out of flow, same as the Saved Pedals subtitle above. */}
          {newMatchOpportunitiesCount > 0 && (
            <span className="dashboard-carousel-subtitle">
              {formatBadgeCount(newMatchOpportunitiesCount)} new trade {newMatchOpportunitiesCount === 1 ? 'opportunity' : 'opportunities'}
            </span>
          )}
        </div>
      
      {/* Loading state */}
      {isLoadingRecommended ? (
        <div className="relative group p-4 flex justify-center" style={{ minHeight: '400px' }}>
          {moLoadSlow && (
            <p style={{ position: 'absolute', top: '0.5rem', left: 0, right: 0, textAlign: 'center', margin: 0, color: '#6b7280', fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem' }}>Still loading on a slow connection…</p>
          )}
          <div className="flex justify-center overflow-x-auto pb-4 min-h-[200px] w-full recommended-scroll-mobile"
            style={{ 
              paddingLeft: '0rem',
              marginLeft: '-6rem',
              paddingRight: '0rem',
              marginRight: '-6rem',
              overflowX: 'auto',
              overflowY: 'hidden'
            }}
          >
            <div className="dashboard-carousel-scroll-track shrink-0 self-start">
            <div className="flex gap-6" style={{ display: 'flex', gap: '32px', alignItems: 'flex-start', justifyContent: 'center' }}>
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="dashboard-carousel-card" style={{ width: '331px' }}>
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
                    <div className="w-full h-36 bg-gray-200"></div>
                    <div className="p-3">
                      <div className="h-3 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="flex justify-between">
                        <div className="h-4 bg-gray-200 rounded w-12"></div>
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>
      ) : moLoadFailed && recommendedPedals.length === 0 ? (
        // No cards at all and the load failed: the band with Try again. With
        // cards present, a failed refresh keeps them and shows the inline
        // "Couldn't refresh" notice above the cards (see below), so useful
        // content stays while the failure is stated (2026-10-31).
        /* FAILED load ≠ no recommendations (Phase 2c); length gate per B3. */
        <SectionLoadFailed />
      ) : recommendedPedals.length === 0 ? (
        <div className="bg-gray-50 rounded-lg text-center" style={{ padding: '0.25rem 2rem 1rem 2rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <p className="text-gray-500 text-lg" style={{ marginBottom: '0.5rem' }}>No recommendations yet</p>
          <p className="text-gray-400 text-sm" style={{ margin: 0 }}>
            When another member has one of your pedals on their Wantlist, their pedals will appear here.
          </p>
        </div>
      ) : (
        <div className="relative group p-4 flex justify-center match-opportunities-pedals-container" style={{ minHeight: '400px' }}>
          {moLoadFailed && recommendedPedals.length > 0 && (
            <div role="status" style={{ margin: '0 0 0.75rem 0', padding: '0.5rem 0.9rem', borderRadius: '10px', background: '#FEF3C7', color: '#92400E', fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span>Couldn&rsquo;t refresh these. Showing what loaded earlier.</span>
              <button type="button" onClick={() => loadRecommendedPedals(null)} style={{ background: 'none', border: 'none', color: '#5A9ECB', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0 }}>Try again</button>
            </div>
          )}
          <style>{`
            @media (max-width: 768px) {
              .recommended-inner-mobile {
                margin: 0 !important;
              }
            }
            
            /* Custom scrollbar for Recommended Pedals */
            #recommended-pedals-scroll::-webkit-scrollbar {
              height: 10px;
            }
            
            #recommended-pedals-scroll::-webkit-scrollbar-track {
              background: linear-gradient(to bottom, transparent 4px, #6B7280 4px, #6B7280 6px, transparent 6px);
            }
            
            #recommended-pedals-scroll::-webkit-scrollbar-thumb {
              background: #C9A227;
              border-radius: 5px;
              border: 2px solid #F7F3EB;
            }
            
            #recommended-pedals-scroll::-webkit-scrollbar-thumb:hover {
              background: #b8911f;
            }
          `}</style>
          <div
            id="recommended-pedals-scroll"
            ref={matchScrollContainerRef}
            className="flex overflow-x-auto w-full recommended-scroll-mobile"
            style={{ 
              paddingLeft: '0rem',
              paddingRight: '0rem',
              paddingBottom: '2rem',
              overflowX: 'auto',
              overflowY: 'hidden',
              minHeight: '200px',
              position: 'relative',
              scrollBehavior: 'auto',
              width: '100%',
              maxWidth: '100%'
            }}
          >
            <div className="dashboard-carousel-scroll-track shrink-0 self-start">
            <div
              className="flex gap-6 recommended-inner-mobile"
              style={{ 
                display: 'flex',
                gap: '32px',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                ...(isMobile
                  ? {}
                  : {
                      minWidth: `${recommendedPedals.length * 331 + (recommendedPedals.length - 1) * 32}px`
                    }),
                width: 'max-content',
                margin: '0'
              }}
            >
              {recommendedPedals.map((pedal) => (
                <div key={pedal.id} className="dashboard-carousel-card" style={{ width: '331px' }}>
                  <RecommendedPedalCard pedal={pedal} showDemandBadge={false} />
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
    </>
  );

  // Define "How It Works" section
  const howItWorksSection = (
    <>
      {/* Subtle Section Divider */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
        <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '2rem 0' }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 section-how-it-works" style={{ paddingTop: '3rem', paddingBottom: '4rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h2 className="font-bold text-gray-900" style={{ fontSize: '2.5rem', fontWeight: '700', letterSpacing: '-0.025em', margin: '0' }}>
            How It Works
          </h2>
        </div>
      <style jsx>{`
        @media (max-width: 768px) {
          .section-additional-matches {
            padding-top: 0 !important;
          }
          .section-latest-pedals {
            padding-top: 0 !important;
          }
          .section-how-it-works {
            padding-top: 1.5rem !important;
            padding-bottom: 1.5rem !important;
          }
          .steps-container {
            flex-direction: column !important;
            gap: 3rem !important;
            padding-left: 1rem !important;
            padding-right: 1rem !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .step-item {
            max-width: 337px !important;
            margin-top: 2rem !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          .step-item img {
            transform: scale(1) !important;
          }
        }
        @media (min-width: 769px) {
          .steps-container {
            gap: 2.52rem !important;
            max-width: 1600px !important;
            margin-left: auto !important;
            margin-right: auto !important;
            align-items: flex-start !important;
          }
          .steps-container .step-item {
            display: flex !important;
            flex-direction: column !important;
          }
          .steps-container .how-it-works-step-img {
            transform: scale(1.5) !important;
          }
          .steps-container .how-it-works-step-img-wrapper {
            height: 220px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin-bottom: 1rem !important;
          }
          .steps-container .how-it-works-step-header {
            font-size: 2rem !important;
            /* Match two-line titles so single-line headings (e.g. Ship & trade) leave space and body copy aligns across columns */
            min-height: calc(2rem * 1.3 * 2) !important;
            box-sizing: border-box !important;
          }
          .steps-container .how-it-works-step-body {
            font-size: 1.375rem !important;
          }
        }
      `}</style>
      
      <div className="steps-container" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '2rem',
        paddingLeft: '4.5rem',
        paddingRight: '4.5rem'
      }}>
        {/* Step 1: List Your Pedals & Wants */}
        <div className="step-item" style={{ 
          textAlign: 'center',
          maxWidth: '337px',
          flex: '1'
        }}>
          <div className="how-it-works-step-img-wrapper" style={{ marginBottom: '1.5rem' }}>
            <img 
              className="how-it-works-step-img"
              src="/images/howitworkspedal.svg" 
              alt="List your pedals"
              style={{ 
                width: '297px',
                height: 'auto',
                maxWidth: '100%', 
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply'
              }}
            />
          </div>
          <h3 className="how-it-works-step-header text-gray-900" style={{
            fontSize: '1.6875rem',
            fontWeight: '600', 
            marginBottom: '0.75rem',
            lineHeight: '1.3'
          }}>
            List your pedals
          </h3>
          <p className="how-it-works-step-body" style={{
            fontSize: '1.1875rem',
            color: '#4b5563',
            lineHeight: '1.6',
            margin: '0'
          }}>
            List the pedals you wish to trade or sell. Then heart the pedals you're after to build your Wantlist, and we'll tell you when one is listed.
          </p>
        </div>

        {/* Step 2: Receive Offers & Matches */}
        <div className="step-item" style={{ 
          textAlign: 'center',
          maxWidth: '337px',
          flex: '1'
        }}>
          <div className="how-it-works-step-img-wrapper" style={{ marginBottom: '1.5rem' }}>
            <img 
              className="how-it-works-step-img"
              src="/images/howitworksmatch.svg" 
              alt="Receive offers and matches"
              style={{ 
                width: '358px',
                height: 'auto',
                maxWidth: '100%', 
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply'
              }}
            />
          </div>
          <h3 className="how-it-works-step-header text-gray-900" style={{
            fontSize: '1.6875rem',
            fontWeight: '600', 
            marginBottom: '0.75rem',
            lineHeight: '1.3'
          }}>
            Browse & send offers
          </h3>
          <p className="how-it-works-step-body" style={{
            fontSize: '1.1875rem',
            color: '#4b5563',
            lineHeight: '1.6',
            margin: '0'
          }}>
            Browse listings and send offers on gear that excites you. Most trades start with a direct offer. If your Wantlist ever overlaps with another member's Pedalboard, we'll show you the trade match too.
          </p>
        </div>

        {/* Step 3: Ship & Trade */}
        <div className="step-item" style={{ 
          textAlign: 'center',
          maxWidth: '337px',
          flex: '1'
        }}>
          <div className="how-it-works-step-img-wrapper" style={{ marginBottom: '1.5rem' }}>
            <img 
              className="how-it-works-step-img"
              src="/images/howitworkstrade.svg" 
              alt="Ship and trade"
              style={{ 
                width: '327px',
                height: 'auto',
                maxWidth: '100%', 
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply'
              }}
            />
          </div>
          <h3 className="how-it-works-step-header text-gray-900" style={{
            fontSize: '1.6875rem',
            fontWeight: '600', 
            marginBottom: '0.75rem',
            lineHeight: '1.3'
          }}>
            Ship & trade
          </h3>
          <p className="how-it-works-step-body" style={{
            fontSize: '1.1875rem',
            color: '#4b5563',
            lineHeight: '1.6',
            margin: '0'
          }}>
            Choose the best offer, whether trade or cash, purchase your fully insured shipping label through the platform, and safely exchange gear using our streamlined, secure process.
          </p>
        </div>
      </div>

      {/* Quick Start Guide + Learn More */}
      <div style={{ textAlign: 'center', marginBottom: '1rem', marginTop: '3rem' }}>
        <button
          type="button"
          onClick={() => openQuickstart({ loggedOutDashboard: !user })}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            marginBottom: '1rem',
            fontSize: 'calc(1rem + 2pt)',
            fontWeight: '600',
            color: '#5A9ECB',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontFamily: 'Nunito, sans-serif',
            display: 'inline-block'
          }}
        >
          Quick Start Guide
        </button>
        <br />
        <a 
          href="/get-started"
          className="hover:opacity-80 transition-opacity"
          style={{
            display: 'inline-block',
            textDecoration: 'none',
            backgroundColor: '#C9A227',
            borderRadius: '9999px',
            border: 'none',
            padding: '12px 24px',
            fontFamily: 'Nunito, sans-serif',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px'
          }}
        >
          Learn More Here
        </a>
      </div>
      </div>
    </>
  );

  // Define "Latest Pedals" section
  const latestPedalsSection = (
    <>
      {/* The zoom:0.75 rule for .dashboard-carousel-card must live here (in
          the always-rendered section) rather than only inside
          wantListMatchesSection (logged-in only). Without it, logged-out
          users see the Browse Pedals carousel cards at full 331px size
          because the wantListMatchesSection style tag never mounts. */}
      <style>{`
        @media (max-width: 768px) {
          .dashboard-carousel-card {
            zoom: 0.75;
          }
        }
      `}</style>
      {/* Subtle Section Divider */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
        <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '1rem 0' }}></div>
      </div>
      
      <div className="mx-auto section-latest-pedals" style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem', paddingLeft: '1.5rem', paddingRight: '1.5rem', backgroundColor: '#F7F3EB' }}>
        <div style={{ 
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
        {/* Section Header */}
        <div className="dashboard-carousel-header-block">
          <div className="dashboard-carousel-eyebrow-row">
            <span className="dashboard-carousel-eyebrow dashboard-carousel-eyebrow--browse-pedals">Just listed</span>
            <Link
              to="/latest-pedals"
              state={{ fresh: true }}
              className="dashboard-carousel-see-all"
            >
              See all →
            </Link>
          </div>
          <h2 className="dashboard-carousel-section-title-heading">
            <Link
              to="/latest-pedals"
              state={{ fresh: true }}
              className="dashboard-carousel-section-title dashboard-carousel-section-title-link"
            >
              Browse Pedals
            </Link>
          </h2>
        </div>
        <div className="relative group p-4 flex justify-center">
        <style>{`
          @media (min-width: 769px) {
            #featured-pedals-scroll {
              max-width: 100vw !important;
              overflow-x: auto !important;
              overflow-y: hidden !important;
              width: 100% !important;
            }
            .latest-pedals-scroll-mobile {
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            .latest-pedals-inner-mobile {
              justify-content: flex-start !important;
            }
          }
          
          @media (max-width: 768px) {
            .latest-pedals-inner-mobile {
              margin: 0 !important;
            }
          }
          
          /* Custom scrollbar for Latest Pedals */
          #featured-pedals-scroll::-webkit-scrollbar {
            height: 10px;
          }
          
          #featured-pedals-scroll::-webkit-scrollbar-track {
            background: linear-gradient(to bottom, transparent 4px, #6B7280 4px, #6B7280 6px, transparent 6px);
          }
          
          #featured-pedals-scroll::-webkit-scrollbar-thumb {
            background: #C9A227;
            border-radius: 5px;
            border: 2px solid #F7F3EB;
          }
          
          #featured-pedals-scroll::-webkit-scrollbar-thumb:hover {
            background: #b8911f;
          }
        `}</style>
        <div
          id="featured-pedals-scroll"
          ref={scrollContainerRef}
          className="flex overflow-x-auto latest-pedals-scroll-mobile"
          style={{ 
            paddingLeft: '0rem',
            marginLeft: '0rem',
            paddingRight: '0rem',
            marginRight: '0rem',
            paddingBottom: '2rem',
            overflowX: 'auto',
            overflowY: 'hidden',
            minHeight: '200px',
            position: 'relative',
            scrollBehavior: 'auto',
            width: '100%',
            maxWidth: '100%'
          }}
        >
          <div className="dashboard-carousel-scroll-track shrink-0 self-start">
          <div
            className="flex gap-6 latest-pedals-inner-mobile"
            style={{
              display: 'flex',
              gap: '32px',
              alignItems: 'flex-start',
              // width: 'max-content' is right for the card row (it feeds the
              // horizontal scroller), but it shrink-wraps a lone child — the
              // couldn't-load band was hugging the left edge while the other
              // sections' bands centered. When the band (or empty copy) is
              // what's showing, behave as a normal full-width centered row.
              ...(featuredPedals.length === 0 && !isLoadingFeatured
                ? { justifyContent: 'center', width: '100%' }
                : {
                    justifyContent: 'flex-start',
                    ...(latestPedalsTrackMinWidth ? { minWidth: latestPedalsTrackMinWidth } : {}),
                    width: 'max-content'
                  }),
              margin: '0'
            }}
          >
          {/* DEBUG: featured-pedals-scroll container rendered */}
          {isLoadingFeatured ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="dashboard-carousel-card" style={{ width: '331px' }}>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
                  <div className="w-full h-36 bg-gray-200"></div>
                  <div className="p-3">
                    <div className="h-3 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="flex justify-between">
                      <div className="h-4 bg-gray-200 rounded w-12"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : jlLoadFailed && featuredPedals.length === 0 ? (
            /* FAILED load ≠ empty marketplace (Phase 2c) — this is the exact
               section the 2026-09-29 incident screenshots caught claiming
               "No pedals" during an offline blip. Length gate per B3. */
            <SectionLoadFailed />
          ) : featuredPedals.length === 0 ? (
            <div className="w-full text-center py-12">
              <p className="text-gray-500 text-lg">No pedals available at the moment.</p>
              <p className="text-gray-400 text-sm mt-2">Check back later for new listings!</p>
            </div>
          ) : (
            featuredPedals.map((pedal) => (
              <div key={pedal.id} className="dashboard-carousel-card" style={{ width: '331px' }}>
                <PedalCard pedal={pedal} currentUser={user} firestoreUser={firestoreUser} />
              </div>
            ))
          )}
          </div>
          </div>
          
        </div>
      </div>
      </div>
    </div>
    </>
  );

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "PedalxChange",
    "url": "https://pedalxchange.com",
    "description": "Trade guitar pedals online with musicians worldwide. Buy, sell, and trade effects pedals safely with escrow protection.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://pedalxchange.com/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PedalxChange",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pedalxchange.com/images/logo-email.png"
      }
    }
  };

  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "PedalxChange",
    "url": "https://pedalxchange.com",
    "logo": "https://pedalxchange.com/images/logo-email.png",
    "description": "Online marketplace for trading guitar effects pedals",
    "sameAs": []
  };

  // Define hero banner section as a variable so we can place it conditionally
  const heroBannerSection = (
    <>
      {/* Banner Section with Image and Overlay Text */}
      <style>{`
        @media (min-width: 769px) {
          #match-opportunities-header {
            margin-bottom: 1rem !important;
          }
          .match-opportunities-pedals-container {
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
          }
        }
        @keyframes slideOverlay {
          0% {
            transform: translateX(calc(-100% - 20px));
          }
          13.33% {
            transform: translateX(0);
          }
          53.33% {
            transform: translateX(0);
          }
          66.66% {
            transform: translateX(calc(100vw - 100% - 20px));
          }
          100% {
            transform: translateX(calc(100vw - 100% - 20px));
          }
        }
        
        @keyframes slideOverlayMobile {
          0% {
            transform: translateX(calc(-100% - 100px));
          }
          16.67% {
            transform: translateX(0);
          }
          58.33% {
            transform: translateX(0);
          }
          75% {
            transform: translateX(calc(100% + 100px));
          }
          100% {
            transform: translateX(calc(100% + 100px));
          }
        }

        @media (min-width: 769px) {
          .desktop-banner-unit {
            display: flex !important;
          }
          
          .banner-gradient-desktop,
          .headline-overlay-desktop,
          .banner-image-desktop {
            display: block !important;
          }
          
          .banner-image-mobile,
          .banner-gradient-mobile,
          .headline-overlay-mobile {
            display: none !important;
          }
        }
        
        @media (max-width: 1400px) and (min-width: 769px) {
          .headline-text-desktop {
            font-size: 2.5rem !important;
          }
          .subtext-desktop {
            font-size: 22px !important;
          }
          .banner-gradient-desktop {
            width: 600px !important;
          }
        }
        
        @media (max-width: 1200px) and (min-width: 769px) {
          .headline-text-desktop {
            font-size: 2.25rem !important;
          }
          .subtext-desktop {
            font-size: 20px !important;
          }
          .banner-gradient-desktop {
            width: 550px !important;
          }
        }
        
        @media (max-width: 1000px) and (min-width: 769px) {
          .headline-text-desktop {
            font-size: 2rem !important;
          }
          .subtext-desktop {
            font-size: 18px !important;
          }
          .banner-gradient-desktop {
            width: 500px !important;
          }
        }
        
        @media (max-width: 900px) and (min-width: 769px) {
          .headline-text-desktop {
            font-size: 1.75rem !important;
          }
          .subtext-desktop {
            font-size: 16px !important;
          }
          .banner-gradient-desktop {
            width: 450px !important;
          }
        }

        @media (max-width: 768px) {
          .desktop-banner-unit {
            display: none !important;
          }
          
          .banner-image-mobile,
          .banner-gradient-mobile,
          .headline-overlay-mobile {
            display: block !important;
          }
          
          .banner-gradient-mobile {
            animation: slideOverlayMobile 12s ease-in-out infinite;
            animation-delay: 1s;
            animation-fill-mode: backwards;
          }
          
          .headline-overlay-mobile {
            animation: slideOverlayMobile 12s ease-in-out infinite;
            animation-delay: 1s;
            animation-fill-mode: backwards;
          }
        }

        @media (max-width: 1400px) and (min-width: 1201px) {
          .headline-overlay-mobile {
            left: 7% !important;
            max-width: 38% !important;
            padding-right: 25px !important;
          }
          .banner-gradient-mobile {
            width: 40% !important;
          }
          .subtext {
            max-width: 100% !important;
            font-size: 22px !important;
            padding-right: 0 !important;
          }
        }
        @media (max-width: 1200px) and (min-width: 1025px) {
          .headline-overlay-mobile {
            left: 6% !important;
            max-width: 36% !important;
            padding-right: 20px !important;
          }
          .banner-gradient-mobile {
            width: 42% !important;
          }
          .subtext {
            max-width: 100% !important;
            font-size: 20px !important;
            padding-right: 0 !important;
          }
        }
        @media (max-width: 1024px) and (min-width: 901px) {
          .headline-overlay-mobile {
            left: 5% !important;
            max-width: 35% !important;
            padding-right: 18px !important;
          }
          .banner-gradient-mobile {
            width: 43% !important;
          }
          .subtext {
            max-width: 100% !important;
            font-size: 18px !important;
            padding-right: 0 !important;
          }
        }
        @media (max-width: 900px) and (min-width: 769px) {
          .headline-overlay-mobile {
            left: 4% !important;
            max-width: 33% !important;
            padding-right: 15px !important;
          }
          .banner-gradient-mobile {
            width: 45% !important;
          }
          .subtext {
            max-width: 100% !important;
            font-size: 16px !important;
            padding-right: 0 !important;
          }
        }
        @media (max-width: 768px) {
          .banner-section {
            height: 290px !important;
            overflow: hidden !important;
            position: relative !important;
            clip-path: inset(20px round 15px);
          }
          .banner-section img {
            top: 20px !important;
            left: 20px !important;
            right: 20px !important;
            width: calc(100% - 40px) !important;
            height: calc(100% - 40px) !important;
          }
          /* Giveaway graphic only (class added when showGiveawayHero). The
             generic rule above stretches the img box to the full card, and
             with objectFit contain the PICTURE floats inside that box, so
             border-radius and the section clip-path round the box's empty
             letterbox pixels while the visible picture keeps square or
             shaved corners (reported 2026-10-02). Fix: size the box to the
             picture itself. Only height is set, so the img keeps its
             intrinsic 1664x1193 ratio, the box equals the picture at every
             viewport width, and the border-radius rounds the actual image.
             The evergreen photo keeps the cover+clip-path path above. */
          .banner-section img.giveaway-hero-img {
            height: min(calc(100% - 40px), calc((100vw - 40px) * 1193 / 1664)) !important;
            width: auto !important;
            top: 50% !important;
            left: 50% !important;
            right: auto !important;
            transform: translate(-50%, -50%);
            border-radius: 15px;
          }
          .banner-gradient-mobile {
            top: 20px !important;
            left: 20px !important;
            right: auto !important;
            bottom: 20px !important;
            width: calc(100% - 40px) !important;
            border-radius: 15px !important;
            animation: slideOverlayMobile 12s ease-in-out infinite;
            animation-delay: 1s;
            animation-fill-mode: backwards;
            overflow: hidden !important;
          }
          .headline-overlay-mobile {
            left: 60px !important;
            right: auto !important;
            top: 25px !important;
            text-align: left !important;
            width: calc(100% - 140px) !important;
            max-width: calc(100% - 140px) !important;
            padding: 0 !important;
            animation: slideOverlayMobile 12s ease-in-out infinite;
            animation-delay: 1s;
            animation-fill-mode: backwards;
            box-sizing: border-box !important;
            overflow: hidden !important;
          }
          .headline-overlay-mobile .headline-text,
          .headline-overlay-mobile h1.headline-text {
            font-size: 1.8125rem !important;
            line-height: 1.3 !important;
            margin-bottom: 0.5rem !important;
          }
          .headline-overlay-mobile .subtext,
          .headline-overlay-mobile p.subtext {
            font-size: 16px !important;
            max-width: 100% !important;
            line-height: 1.5 !important;
            margin: 0 !important;
            word-break: normal !important;
            overflow-wrap: break-word !important;
            hyphens: none !important;
            white-space: normal !important;
          }
          .headline-overlay-mobile .subtext br:first-of-type,
          .headline-overlay-mobile p.subtext br:first-of-type {
            display: block !important;
          }
          .headline-overlay-mobile .subtext br.mobile-break:last-of-type,
          .headline-overlay-mobile p.subtext br.mobile-break:last-of-type {
            display: block !important;
          }
          .mission-logo-row {
            flex-direction: column !important;
            padding-left: 1rem !important;
            padding-right: 1rem !important;
            /* gap removed - handled by responsive styles below */
          }
          .mission-text-column {
            max-width: 100% !important;
            margin-left: 0 !important;
          }
          .mission-text-column p {
            font-size: 16px !important;
          }
          .mission-text-column p.text-gray-600 {
            font-size: 14px !important;
          }
        }
      `}</style>
      <div 
        className="banner-section" 
        style={{ 
          position: 'relative',
          width: '100%',
          height: '380px',
          overflow: 'visible',
          marginBottom: '0.5rem',
          WebkitTransform: 'translateZ(0)',
          backgroundColor: '#F7F3EB',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {/* Desktop: Combined unit wrapper (centered) */}
        {/* Whole unit clickable while the giveaway hero is up (web only),
            matching the previous (Strymon) giveaway build — the graphic's
            "DETAILS HERE" must work wherever the user taps. Remove
            onClick/cursor together with the giveaway image when the giveaway
            ends. */}
        <div
          className="desktop-banner-unit"
          onClick={heroClick}
          style={{
            position: 'relative',
            display: 'flex',
            height: 'calc(100% - 40px)',
            maxWidth: 'calc(100% - 40px)',
            alignItems: 'center',
            cursor: heroCursor
          }}
        >
          {/* Cream Overlay Block (Desktop - left side) */}
          <div className="banner-gradient-desktop" style={{
            position: 'relative',
            width: '650px',
            height: '100%',
            backgroundColor: 'rgba(235, 230, 220, 0.92)',
            zIndex: 1,
            borderRadius: '15px 0 0 15px',
            flexShrink: 1
          }}>
            {/* Text overlay inside cream block (Desktop) */}
            <div className="headline-overlay-desktop" style={{
              position: 'absolute',
              zIndex: 2,
              left: '8%',
              top: '47%',
              transform: 'translateY(-50%)',
              textAlign: 'left',
              maxWidth: '90%',
              boxSizing: 'border-box',
              paddingRight: '20px'
            }}>
              <h1 className="font-bold headline-text-desktop" style={{ 
                fontSize: '2.875rem', 
                lineHeight: '1.2', 
                marginBottom: '0.5rem',
                color: '#C9A227',
                textAlign: 'left'
              }}>
                Trade Pedals.<br />Not Dollars.
              </h1>
              <p className="subtext-desktop" style={{ 
                fontSize: '24px', 
                maxWidth: '100%', 
                margin: '0',
                lineHeight: '1.6',
                color: 'rgba(55, 65, 81, 0.95)',
                textAlign: 'left',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                wordBreak: 'normal',
                whiteSpace: 'normal',
                hyphens: 'none'
              }}>
                We connect musicians through safe, low-cost trades, exchanging unused pedals for fresh inspiration. <span style={{ color: '#C9A227', fontWeight: '700' }}>Play more. Spend less.</span>
              </p>
            </div>
          </div>
          
          {/* Hero image (Desktop - right side). On web this is temporarily the
              giveaway promo graphic — it has "DETAILS HERE" baked into the
              pixels, so it stays clickable through to /giveaway for as long as
              it's up; native keeps the evergreen hero (see showGiveawayHero).
              Restore dashboard_home_2.jpg everywhere when the giveaway ends. */}
          <img
            src={heroImageSrc}
            alt={heroImageAlt}
            className="banner-image-desktop"
            onClick={heroClick}
            style={{
              position: 'relative',
              height: '100%',
              width: 'auto',
              objectFit: 'contain',
              objectPosition: 'left center',
              zIndex: 0,
              opacity: 0.94,
              borderRadius: '0 15px 15px 0',
              flexShrink: 1,
              cursor: heroCursor
            }}
          />
        </div>
        
        {/* Mobile: Hero image. Giveaway promo graphic on web (see desktop note
            above). While the giveaway graphic is up, objectFit is 'contain',
            not the usual 'cover': the slot is ~1:1 on phones and the 1.4:1
            graphic has "GIVEAWAY" / "DETAILS HERE" text at its edges —
            cover-cropping cut the text off. Native keeps cover + the evergreen
            photo. */}
        <img
          src={heroImageSrc}
          alt={heroImageAlt}
          className={`banner-image-mobile${showGiveawayHero ? ' giveaway-hero-img' : ''}`}
          onClick={heroClick}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            right: '20px',
            width: 'calc(100% - 40px)',
            height: 'calc(100% - 40px)',
            objectFit: showGiveawayHero ? 'contain' : 'cover',
            objectPosition: '50% 50%',
            zIndex: 0,
            opacity: 0.94,
            borderRadius: '15px',
            display: 'none',
            cursor: heroCursor
          }}
        />
        
        {/* Cream Overlay Block (Mobile - over image) */}
        <div
          className="banner-gradient-mobile"
          onClick={heroClick}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            width: '35%',
            bottom: '20px',
            // Fully opaque while the giveaway hero is up: the promo graphic
            // has baked-in text and a bright white pill that ghost through
            // the usual 92% cream as a white blob and double-exposed
            // lettering (mobile web report, 2026-10-02). The evergreen photo
            // keeps the original translucency.
            backgroundColor: showGiveawayHero ? 'rgb(235, 230, 220)' : 'rgba(235, 230, 220, 0.92)',
            zIndex: 1,
            borderRadius: '15px 0 0 15px',
            display: 'none',
            cursor: heroCursor
          }}
        ></div>
        
        {/* Headline Overlay on Banner (Mobile - over image) */}
        <div
          className="headline-overlay-mobile"
          onClick={heroClick}
          style={{
            position: 'absolute',
            zIndex: 2,
            left: '8%',
            top: '25px',
            textAlign: 'left',
            maxWidth: '400px',
            boxSizing: 'border-box',
            paddingRight: '20px',
            display: 'none',
            cursor: heroCursor
          }}
        >
            <h1 className="font-bold headline-text" style={{ 
              fontSize: '2.875rem', 
              lineHeight: '1.2', 
              marginBottom: '0.5rem',
              color: '#C9A227',
              textAlign: 'left'
            }}>
              Trade Pedals.<br />Not Dollars.
            </h1>
            <p className="subtext" style={{ 
              fontSize: '24px', 
              maxWidth: '100%', 
              margin: '0',
              lineHeight: '1.6',
              color: 'rgba(55, 65, 81, 0.95)',
              textAlign: 'left',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              hyphens: 'auto'
            }}>
              <style>{`
                @media (min-width: 769px) {
                  .headline-overlay .subtext br,
                  .headline-overlay p.subtext br {
                    display: block !important;
                  }
                  .headline-overlay .subtext,
                  .headline-overlay p.subtext {
                    font-weight: normal !important;
                  }
                }
                @media (max-width: 768px) {
                  .headline-overlay .subtext,
                  .headline-overlay p.subtext {
                    font-weight: bold !important;
                  }
                }
                @media (min-width: 1601px) {
                  .subtext {
                    font-size: 26px !important;
                  }
                }
                @media (max-width: 1600px) and (min-width: 1501px) {
                  .subtext {
                    font-size: 24px !important;
                  }
                  .headline-text {
                    font-size: 2.75rem !important;
                  }
                }
                @media (max-width: 1500px) and (min-width: 1401px) {
                  .subtext {
                    font-size: 23px !important;
                  }
                  .headline-text {
                    font-size: 2.625rem !important;
                  }
                }
                @media (max-width: 1400px) and (min-width: 1301px) {
                  .subtext {
                    max-width: 100% !important;
                    font-size: 22px !important;
                  }
                  .headline-text {
                    font-size: 2.5rem !important;
                  }
                }
                @media (max-width: 1300px) and (min-width: 1201px) {
                  .subtext {
                    max-width: 100% !important;
                    font-size: 21px !important;
                  }
                  .headline-text {
                    font-size: 2.375rem !important;
                  }
                }
                @media (max-width: 1200px) and (min-width: 1101px) {
                  .subtext {
                    max-width: 100% !important;
                    font-size: 20px !important;
                  }
                  .headline-text {
                    font-size: 2.25rem !important;
                  }
                }
                @media (max-width: 1100px) and (min-width: 1025px) {
                  .subtext {
                    max-width: 100% !important;
                    font-size: 19px !important;
                  }
                  .headline-text {
                    font-size: 2.125rem !important;
                  }
                }
                @media (max-width: 1024px) and (min-width: 901px) {
                  .subtext {
                    max-width: 100% !important;
                    font-size: 18px !important;
                  }
                  .headline-text {
                    font-size: 2rem !important;
                  }
                }
                @media (max-width: 900px) and (min-width: 769px) {
                  .subtext {
                    max-width: 100% !important;
                    font-size: 16px !important;
                  }
                  .headline-text {
                    font-size: 1.875rem !important;
                  }
                }
              `}</style>
              We connect musicians through safe, low-cost trades, exchanging unused pedals for fresh inspiration. <br className="mobile-break" /><span style={{ color: '#C9A227', fontWeight: '700' }}>Play more. Spend less.</span>
            </p>
        </div>
      </div>

      {/* Mission Text and Logo Section (Below Banner) */}
      <div className="mission-outer-wrapper" style={{ backgroundColor: '#F7F3EB', marginBottom: '4rem' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8 py-2 mission-section-wrapper">
          <style>{`
            @media (min-width: 769px) {
              .mission-section-wrapper.py-2 {
                padding-top: 0 !important;
                margin-top: -2rem !important;
              }
              .mission-outer-wrapper {
                margin-bottom: 1.5rem !important;
              }
              .section-how-it-works {
                padding-top: 1rem !important;
                padding-bottom: 1.5rem !important;
              }
            }
            @media (max-width: 768px) {
              .mission-section-wrapper.py-2 {
                padding-top: 0 !important;
                margin-top: -1rem !important;
              }
              .mission-outer-wrapper {
                margin-bottom: 2rem !important;
              }
              .mission-section-wrapper.px-6 {
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
              .mission-section-wrapper > * {
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
            }
          `}</style>
          <div className="mission-content" style={{ maxWidth: '1600px', margin: '0 auto', width: '100%' }}>
            <style>{`
              @media (max-width: 768px) {
                .mission-content {
                  padding-left: 0 !important;
                  padding-right: 0 !important;
                }
              }
              .mission-logo-row {
                align-items: flex-start !important;
              }
              /* Gap is now controlled by React state - no CSS needed */
            `}</style>

            {/* For logged-out users only - Mission row below */}
            {!user && (
              <>
                <div className="mission-logo-row" style={{ display: 'flex', width: '100%', gap: isMobile ? '1.5rem' : gapSize, justifyContent: 'space-between', alignItems: 'flex-start', paddingLeft: isMobile ? '0' : '1rem', paddingRight: isMobile ? '0' : '1rem' }}>
                  <style>{`
                    @media (max-width: 768px) {
                      .mission-logo-row[style] {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                      }
                    }
                  `}</style>
                  {/* Left Column - Successful Trades or Logo */}
              <div className="logo-column" style={{ flex: '0 0 auto', display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start', alignItems: 'center', marginLeft: isMobile ? '0' : '1rem', marginTop: '2rem', flexDirection: 'column', width: isMobile ? '100%' : 'auto' }}>
                {isLoadingTrades ? (
                  <div style={{ width: '478px', height: '450px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ color: '#6B7280' }}>Loading trades...</p>
                  </div>
                ) : successfulTrades.length === 0 ? (
                  <img 
                    src="/images/logo.svg" 
                    alt="PedalxChange Logo" 
                    className="dashboard-logo"
                    style={{ 
                      height: '280px', 
                      width: 'auto',
                      maxWidth: '100%'
                    }} 
                  />
                ) : (
                  <div className="recent-trades-container" style={{ width: '585px', minHeight: '500px', paddingLeft: '1rem', paddingRight: '1rem' }}>
                    {/* 585px = 553px of content + the 2rem of padding (global border-box
                        sizing puts padding INSIDE the width). The carousel's desktop pixel
                        math needs exactly 553px of content: 336px large img + 15px gap +
                        202px small img (right-anchored at calc(100% - 202px)), arrow at
                        294px bridging both edges. At the old 553px the content was only
                        521px, which slid the right-anchored small img 32px left onto the
                        large img and buried the arrow. Mobile overrides width/padding. */}
                    <style>{`
                      .recent-trades-arrow-img {
                        height: 160px;
                        object-fit: contain;
                      }
                      .recent-trades-carousel-wrapper {
                        overflow: hidden;
                        width: 100%;
                        position: relative;
                        box-sizing: border-box;
                        margin: 0;
                        padding: 0;
                        /* Paint containment is a stricter clipping promise to the browser than
                           plain overflow:hidden — browsers can prune paint operations entirely
                           outside the box, which kills the sub-pixel sliver that was leaking
                           the next slide's left edge after many cycles. */
                        contain: paint;
                        /* Final defensive crop: shave 1px from the right edge. The sub-pixel
                           anti-aliasing of the small-img's right edge (which lives flush against
                           the slide boundary) was visible as a thin sliver on certain trades
                           whose right-edge image content contrasted with the beige page bg.
                           Clipping that 1px guarantees the line cannot render regardless of
                           image content, browser zoom, or DPI. The small-img loses 1px out of
                           202px of width — visually imperceptible. */
                        clip-path: inset(0 1px 0 0);
                        isolation: isolate;
                      }
                      .recent-trades-carousel-inner {
                        display: flex;
                        transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
                        will-change: transform;
                        box-sizing: border-box;
                        margin: 0;
                        padding: 0;
                        /* Explicit width pinned to parent — prevents implicit-auto width from
                           resolving to a fractional value, which would compound sub-pixel
                           rounding across each translateX transition and leak the next slide
                           in along the right edge after 5+ cycles. */
                        width: 100%;
                        max-width: 100%;
                        /* Pin the transform reference point to top-left. Default is 50% 50%, which
                           reintroduces sub-pixel calculations into percentage-based translates. */
                        transform-origin: 0 0;
                        /* Force the GPU compositor to render this layer at integer pixel boundaries
                           (translate3d alone doesn't always do this on macOS retina). */
                        backface-visibility: hidden;
                        -webkit-backface-visibility: hidden;
                      }
                      .recent-trades-carousel-item {
                        flex: 0 0 100%;
                        width: 100%;
                        min-width: 100%;
                        max-width: 100%;
                        box-sizing: border-box;
                        overflow: hidden;
                        position: relative;
                        margin: 0;
                        padding: 0;
                        flex-shrink: 0;
                      }
                      @media (max-width: 768px) {
                        .recent-trades-carousel-item {
                          overflow: hidden;
                        }
                        .recent-trades-carousel-wrapper {
                          overflow: hidden !important;
                        }
                      }
                      @media (max-width: 768px) {
                        .recent-trades-container {
                          width: 100% !important;
                          max-width: 100% !important;
                          padding: 0 1rem !important;
                          margin: 0 auto !important;
                          display: flex !important;
                          flex-direction: column !important;
                          align-items: center !important;
                          text-align: center !important;
                          box-sizing: border-box !important;
                          overflow-x: hidden !important;
                        }
                        .recent-trades-carousel-wrapper {
                          width: 100% !important;
                          max-width: 100% !important;
                          overflow: hidden !important;
                          box-sizing: border-box !important;
                        }
                        .recent-trades-carousel-inner {
                          width: 100% !important;
                          max-width: 100% !important;
                          box-sizing: border-box !important;
                        }
                        .recent-trades-header {
                          font-size: 2.625rem !important;
                          margin-bottom: 1.5rem !important;
                          text-align: center !important;
                          width: 100% !important;
                        }
                        .recent-trades-images-container {
                          height: 200px !important;
                          width: 330px !important;
                          max-width: calc(100% - 2rem) !important;
                          margin: 0 auto 2rem auto !important;
                          position: relative !important;
                          left: 0 !important;
                          overflow: visible !important;
                          align-items: flex-start !important;
                          padding-top: 0 !important;
                          box-sizing: border-box !important;
                        }
                        .recent-trades-large-img {
                          width: 200px !important;
                          height: 200px !important;
                        }
                        .recent-trades-small-img {
                          left: 210px !important;
                        }
                        /* Direct-child only — multi-pedal stacks nest imgs inside .recent-trades-stack-small,
                           which must keep its own percentage-based sizing instead of being forced to 120x120. */
                        .recent-trades-small-img > img {
                          width: 120px !important;
                          height: 120px !important;
                        }
                        /* Multi-pedal stack wrappers — match the single-pedal sizes on each side
                           so the percentage-positioned cards inside scale identically. */
                        .recent-trades-stack-large {
                          width: 200px !important;
                          height: 200px !important;
                        }
                        .recent-trades-stack-small {
                          width: 120px !important;
                          height: 120px !important;
                        }
                        .recent-trades-arrow {
                          left: 175px !important;
                        }
                        .recent-trades-arrow img,
                        .recent-trades-arrow-img {
                          width: 60px !important;
                          height: 150px !important;
                          max-height: none !important;
                          object-fit: fill !important;
                        }
                        .recent-trades-info {
                          flex-direction: column !important;
                          align-items: center !important;
                          text-align: center !important;
                          padding-left: 0 !important;
                          max-width: 100% !important;
                          gap: 1rem !important;
                        }
                        .recent-trades-feedback {
                          text-align: center !important;
                          width: 100% !important;
                        }
                      }
                    `}</style>
                    {/* Header */}
                    <h3 className="recent-trades-header font-bold text-gray-900" style={{ 
                      fontSize: '2.625rem', 
                      fontWeight: '700', 
                      marginBottom: '2rem',
                      textAlign: 'center',
                      letterSpacing: '-0.025em'
                    }}>
                      Recent Trades
                    </h3>
                    
                    {/* Carousel Wrapper */}
                    <div className="recent-trades-carousel-wrapper" ref={recentTradesCarouselRef}>
                      <div 
                        className="recent-trades-carousel-inner"
                        style={{
                          // Pixel-based transform (instead of percentage) once the carousel
                          // viewport has been measured. Math.round in the measure step keeps
                          // the offset on integer pixels so cumulative sub-pixel rounding can
                          // never accumulate across many auto-scroll cycles. Pre-measure
                          // (slideWidth = 0) we fall back to the percentage form so initial
                          // render is unbroken even if the ResizeObserver hasn't fired yet.
                          transform: recentTradesSlideWidth > 0
                            ? `translate3d(-${currentTradeIndex * recentTradesSlideWidth}px, 0, 0)`
                            : `translate3d(-${currentTradeIndex * 100}%, 0, 0)`,
                          // During wraparound (last slide → 0) suppress the CSS transition so
                          // the slide jumps instantly instead of sweeping backwards through
                          // every prior slide over 0.8s.
                          transition: carouselSnapping ? 'none' : undefined
                        }}
                      >
                        {successfulTrades.map((trade, index) => {
                          // Multi-pedal aware: pedalsReceived/pedalsGiven are arrays.
                          // Multi-for-one rule means only one side is ever multi; the other is always [single].
                          const receivedPedals = trade.pedalsReceived;
                          const givenPedals = trade.pedalsGiven;
                          const isCurrent = index === currentTradeIndex;
                          const isAdjacent = index === currentTradeIndex + 1;
                          const withImages = revealedSlides.has(index);
                          
                          return (
                            <div key={`trade-${trade.id}-${index}`} className="recent-trades-carousel-item" style={{ position: 'relative', width: '100%', maxWidth: '100%', overflow: 'hidden', flexShrink: 0 }}>
                          {/* Pedals with Arrow */}
                          <div className="recent-trades-images-container" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', marginBottom: '2rem', position: 'relative', height: '336px', width: '100%', maxWidth: '100%', overflow: 'visible' }}>
                            {/* Small pedal(s) (given) - rendered FIRST so the browser starts decoding it before the larger received image */}
                            {/* left: calc(100% - 202px) anchors the small image to the slide's right edge.
                                Hardcoding left:351px assumed slide width = 553px exactly; if the slide ever
                                renders at a fractional pixel width (which happens on retina + browser zoom),
                                the small-img extends past the slide boundary by 0.x px and shows up as a thin
                                vertical sliver on the right edge after enough cycles. The mobile CSS override
                                .recent-trades-small-img { left: 210px !important } still wins on mobile. */}
                            <div className="recent-trades-small-img" style={{ position: 'absolute', left: 'calc(100% - 202px)', top: 'calc(50% - 30px)', transform: 'translateY(-50%)', zIndex: 3 }}>
                              {renderPedalStack(givenPedals, 'small', { tradeId: trade.id, isCurrent, isAdjacent, withImages })}
                            </div>
                            
                            {/* Large pedal(s) (received) - aligned left */}
                            <div style={{ position: 'relative', zIndex: 1 }}>
                              {renderPedalStack(receivedPedals, 'large', { tradeId: trade.id, isCurrent, isAdjacent, withImages })}
                            </div>
                            
                            {/* Arrow - positioned between pedals */}
                            <div className="recent-trades-arrow" style={{ 
                              position: 'absolute',
                              left: '294px',
                              top: 'calc(50% - 30px)',
                              transform: 'translateY(-50%)',
                              zIndex: 4
                            }}>
                              <img 
                                src="/images/recent_trades_arrow.svg" 
                                alt="Double arrow" 
                                className="recent-trades-arrow-img"
                                style={{
                                  width: '100px',
                                  height: 'auto',
                                  objectFit: 'contain'
                                }}
                              />
                            </div>
                          </div>
                          
                          {/* User Info and Feedback */}
                          <div className="recent-trades-info" style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', maxWidth: '553px', paddingLeft: '30px' }}>
                            {/* Left: User Profile */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
                              {/* Username */}
                              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.6rem' }}>
                                {trade.reviewerProfile.username || 'Unknown'}
                              </div>
                              
                              {/* Membership Badge */}
                              <div style={{ marginBottom: '0.6rem', transform: 'scale(1.0)' }}>
                                {trade.reviewerProfile.membershipTier && trade.reviewerProfile.membershipTier !== 'free' ? (
                                  getMembershipBadge(trade.reviewerProfile.membershipTier)
                                ) : (
                                  <div className="flex items-center bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
                                    Free Tier
                                  </div>
                                )}
                              </div>
                              
                              {/* Profile Picture with Badges */}
                              <div style={{ position: 'relative', width: '67px', height: '67px', marginBottom: '0.6rem' }}>
                                <img
                                  key={`profile-${trade.id}`}
                                  src={withImages ? (trade.reviewerProfile.photoURL || '/images/default-avatar.png') : BLANK_IMAGE_SRC}
                                  alt={trade.reviewerProfile.username}
                                  style={{
                                    width: '67px',
                                    height: '67px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '2px solid #D1D5DB'
                                  }}
                                />
                                
                                {/* Membership Badge Overlay - top left */}
                                {getMembershipProfileBadge(trade.reviewerProfile.membershipTier)}
                                
                                {/* Trade Count Badge - bottom right */}
                                {(trade.reviewerProfile.badge || trade.reviewerProfile.completedTrades || 0) > 0 && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '-5px',
                                      right: '-5px',
                                      backgroundColor: 'gold',
                                      color: '#000',
                                      fontWeight: 'bold',
                                      borderRadius: '50%',
                                      width: '22px',
                                      height: '22px',
                                      fontSize: '0.8rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: '2px solid #fff',
                                      boxShadow: '0 0 5px rgba(0,0,0,0.3)'
                                    }}
                                  >
                                    {trade.reviewerProfile.badge || trade.reviewerProfile.completedTrades || 0}
                                  </div>
                                )}
                              </div>
                              
                              {/* User Rating */}
                              {trade.reviewerProfile.averageRating > 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem' }}>
                                  <span style={{ color: '#F59E0B' }}>★</span>
                                  <span style={{ fontWeight: '600', color: '#374151' }}>
                                    {trade.reviewerProfile.averageRating.toFixed(1)}
                                  </span>
                                </div>
                              ) : (
                                <div style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>
                                  No ratings yet
                                </div>
                              )}
                            </div>
                            
                            {/* Right: Feedback */}
                            <div className="recent-trades-feedback" style={{ flex: 1 }}>
                              {/* Stars Given - centered horizontally */}
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '0.8rem' }}>
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <span key={i} style={{ color: '#F59E0B', fontSize: '1.4rem' }}>★</span>
                                ))}
                              </div>
                              
                              {/* Feedback Text — clamped to 3 lines so long
                                  comments don't blow out the card height and
                                  create a gap below the carousel on mobile. */}
                              <p style={{ 
                                fontSize: '0.9rem', 
                                color: '#374151', 
                                lineHeight: '1.5',
                                margin: 0,
                                fontStyle: 'italic',
                                textAlign: 'center',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                "{trade.reviewerFeedback.comment || 'Great trade!'}"
                              </p>
                            </div>
                          </div>
                        </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Right Column - Mission Text */}
              <div className="mission-text-column" style={{ flex: isMobile ? 'none' : '1', textAlign: isMobile ? 'center' : 'left', marginLeft: isMobile ? '0' : missionMarginLeft, marginRight: isMobile ? '0' : '2rem', paddingTop: '3rem', paddingRight: isMobile ? '1rem' : '1rem', width: isMobile ? '100%' : 'auto' }}>
                <p className="text-gray-700" style={{ 
                  fontSize: '22px',
                  lineHeight: '1.6',
                  marginBottom: '1.5rem'
                }}>
                  <style>{`
                    @media (min-width: 768px) {
                      .mission-text-column p {
                        font-size: 24px !important;
                      }
                    }
                  `}</style>
                  <span style={{ color: '#C9A227', fontWeight: 'bold' }}>Trade Pedals. Not Dollars.</span>{' '}
                  Your next favorite pedal is already out there. Trade the gear collecting dust for something that inspires you, at a fraction of the cost of buying new.
                </p>
                <p className="text-gray-700" style={{ 
                  fontSize: '22px',
                  lineHeight: '1.6',
                  marginBottom: '1.5rem'
                }}>
                  <style>{`
                    @media (min-width: 768px) {
                      .mission-text-column p {
                        font-size: 24px !important;
                      }
                    }
                  `}</style>
                  <span style={{ color: '#C9A227', fontWeight: 'bold' }}>Built to keep gear moving.</span>{' '}
                  Trade, buy, or sell. PXC supports it all. Trading costs a flat $3. If you&apos;d rather buy or sell, our fees are half those of the leading marketplace. More money stays in your pocket either way.
                </p>
                <p className="text-gray-700" style={{ 
                  fontSize: '22px',
                  lineHeight: '1.6',
                  marginBottom: '1.5rem'
                }}>
                  <style>{`
                    @media (min-width: 768px) {
                      .mission-text-column p {
                        font-size: 24px !important;
                      }
                    }
                  `}</style>
                  <span style={{ color: '#C9A227', fontWeight: 'bold' }}>Every transaction is protected.</span>{' '}
                  Verified users, secure Stripe payments, insured shipping, and optional TradeProtect coverage. We&apos;ve handled the hard parts so you can focus on the fun part—the gear.
                </p>
                <p className="text-gray-700" style={{ 
                  fontSize: '22px',
                  lineHeight: '1.6',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  <style>{`
                    @media (min-width: 768px) {
                      .mission-text-column p.referral-cta-text {
                        font-size: 24px !important;
                      }
                    }
                  `}</style>
                  <span className="referral-cta-text" style={{ color: '#C9A227', fontStyle: 'italic' }}>Sign up and refer a friend to PXC to trade for free!</span>
                </p>
                
                {/* Spacer to push content down */}
                <div className="mission-spacer" style={{ height: '6rem' }}></div>
                
                {/* Membership CTA Section - pushed down */}
                <div style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                  <p className="text-gray-600" style={{ 
                    fontSize: '18px',
                    lineHeight: '1.6',
                    textAlign: 'center'
                  }}>
                    <style>{`
                      @media (min-width: 768px) {
                        .mission-text-column p.text-gray-600 {
                          font-size: 20px !important;
                        }
                      }
                    `}</style>
                    {user ? (
                      <>For added benefits and features like reduced fees and promoted match listings, visit the <Link to="/profile" className="hover:underline font-medium" style={{ color: '#5A9ECB' }}>profile</Link> page to become a paid member.</>
                    ) : (
                      <>For added benefits and features like reduced fees and promoted match listings, join our membership program.</>
                    )}
                  </p>
                  
                  {/* Sign Up Call to Action - Only for logged out users */}
                  {!user && (
                    <div className="signup-cta" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}>
                    <style>{`
                      @media (max-width: 768px) {
                        .signup-cta {
                          flex-direction: column !important;
                          align-items: center !important;
                          gap: 0.75rem !important;
                          text-align: center !important;
                        }
                        .signup-cta span {
                          font-size: 16px !important;
                        }
                        .logo-column {
                          order: -1 !important;
                          margin-top: 0 !important;
                          margin-bottom: 2rem !important;
                          margin-left: 0 !important;
                          justify-content: center !important;
                        }
                        .dashboard-logo {
                          height: 180px !important;
                        }
                      }
                    `}</style>
                    <span className="font-semibold text-gray-700" style={{ fontSize: '19px', fontWeight: 'bold' }}>
                      Sign up here to start trading
                    </span>
                    <a 
                      href="/signup" 
                      className="auth-button"
                      style={{
                        color: '#5A9ECB',
                        textDecoration: 'none',
                        fontSize: '1.125rem',
                        fontWeight: '500',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        border: '1px solid #5A9ECB',
                        backgroundColor: 'transparent',
                        transition: 'all 0.2s',
                        display: 'inline-block',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#5A9ECB';
                        e.target.style.color = 'white';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#5A9ECB';
                      }}
                    >
                      Sign Up / Sign In
                    </a>
                  </div>
                  )}
                  
                  {/* US-based Users Note - Only for logged out users */}
                  {!user && (
                    <div style={{ textAlign: 'center', marginTop: '1rem', marginBottom: '1rem' }}>
                      <span style={{ fontSize: '11px !important', color: '#666', fontStyle: 'italic', fontWeight: 'normal', lineHeight: '1.2', display: 'block' }}>
                        🇺🇸 Currently available to US-based users only
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
              </>
            )}
            
            {/* Responsive styling */}
            <style jsx>{`
              /* Desktop - explicit positioning */}
              @media (min-width: 769px) {
                .headline-overlay-mobile {
                  position: absolute !important;
                  left: 8% !important;
                  right: auto !important;
                  top: 125px !important;
                  text-align: left !important;
                  text-align: left !important;
                }
              }
              
              /* Mobile */
              @media (max-width: 768px) {
                .mission-content {
                  width: 100% !important;
                  max-width: 100% !important;
                  padding: 0 !important;
                }
                .mission-logo-row {
                  flex-direction: column !important;
                  gap: 1.5rem !important;
                  padding-left: 0 !important;
                  padding-right: 0 !important;
                  justify-content: center !important;
                  align-items: center !important;
                  width: 100% !important;
                  max-width: 100% !important;
                  margin: 0 auto !important;
                }
                .mission-text-column {
                  max-width: 100% !important;
                  width: 100% !important;
                  padding: 0 1rem !important;
                  text-align: center !important;
                  margin-top: 0 !important;
                  margin-left: 0 !important;
                  margin-right: 0 !important;
                  flex: none !important;
                  box-sizing: border-box !important;
                }
                .logo-column {
                  width: 100% !important;
                  max-width: 100% !important;
                }
                .mission-text-column p {
                  font-size: 16px !important;
                }
                .mission-text-column p.text-gray-600 {
                  font-size: 14px !important;
                }
                .mission-spacer {
                  height: 2rem !important;
                }
                .logo-column {
                  width: 100% !important;
                  justify-content: center !important;
                  margin-top: 0 !important;
                  margin-left: 0 !important;
                  margin-right: 0 !important;
                  display: flex !important;
                  align-items: center !important;
                  flex: none !important;
                }
                .dashboard-logo {
                  height: 140px !important;
                }
                .auth-button {
                  font-size: 0.875rem !important;
                  padding: 0.375rem 0.75rem !important;
                }
              }
            `}</style>
          </div>
        </div>
      </div>
    </>
  );

  const loggedInCarouselLayout = useMemo(() => {
    if (isLoadingWantListMatches || isLoadingRecommended) return 'full';
    // A FAILED section is unconfirmed, not "no content" (Phase 2c, found in
    // the 2026-09-29 localhost validation): demoting to the sparse layouts
    // reshuffled the whole page — Just Listed first, "How It Works" second —
    // which read as the logged-out view. Keep the standard order so the
    // couldn't-load bands render in the sections' normal slots. A genuine
    // server-confirmed empty still picks the sparse layouts below.
    if (wlmLoadFailed || moLoadFailed) return 'full';
    const hasWant = wantListMatches.length > 0;
    const hasRec = recommendedPedals.length > 0;
    if (hasWant && hasRec) return 'full';
    if (hasWant && !hasRec) return 'noRec';
    if (!hasWant && hasRec) return 'noWant';
    return 'neither';
  }, [isLoadingWantListMatches, isLoadingRecommended, wantListMatches.length, recommendedPedals.length, wlmLoadFailed, moLoadFailed]);

  const loggedInHeroWrap = (
    <div style={{ marginTop: '2rem' }}>
      {heroBannerSection}
    </div>
  );

  const loggedInMissionSection = (
    <>
    {/* Mission/Recent Trades Section */}
    <style>{`
      @media (min-width: 769px) {
        .mission-content-logged-in {
          margin-top: 0.5rem !important;
        }
      }
      @media (max-width: 768px) {
        .mission-content-logged-in {
          margin-top: -0.5rem !important;
        }
        .mission-content-logged-in .logo-column {
          margin-top: 0 !important;
          margin-bottom: 0.5rem !important;
        }
        .mission-content-logged-in .mission-text-column {
          padding-top: 0 !important;
        }
      }
    `}</style>
    <div className="mission-content mission-content-logged-in" style={{ maxWidth: '1600px', margin: '0 auto', width: '100%', marginTop: '2rem' }}>
      <div className="mission-logo-row" style={{ display: 'flex', width: '100%', gap: isMobile ? '1.5rem' : gapSize, justifyContent: 'space-between', alignItems: 'flex-start', paddingLeft: isMobile ? '0' : '1rem', paddingRight: isMobile ? '0' : '1rem' }}>
        {/* Left Column - Successful Trades or Logo */}
        <div className="logo-column" style={{ flex: '0 0 auto', display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start', alignItems: 'center', marginLeft: isMobile ? '0' : '1rem', marginTop: '2rem', flexDirection: 'column', width: isMobile ? '100%' : 'auto' }}>
          {isLoadingTrades ? (
            <div style={{ width: '478px', height: '450px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ color: '#6B7280' }}>Loading trades...</p>
            </div>
          ) : successfulTrades.length === 0 ? (
            <img 
              src="/images/logo.svg" 
              alt="PedalxChange Logo" 
              className="dashboard-logo"
              style={{ 
                height: '280px', 
                width: 'auto',
                maxWidth: '100%'
              }} 
            />
          ) : (
            <div className="recent-trades-container" style={{ width: '585px', minHeight: '500px', paddingLeft: '1rem', paddingRight: '1rem' }}>
              {/* 585px, not 553px — same border-box padding math as the logged-out
                  copy of this section above (553px of content + 2rem padding). */}
              <style>{`
                .recent-trades-arrow-img {
                  height: 160px;
                  object-fit: contain;
                }
                .recent-trades-carousel-wrapper {
                  overflow: hidden;
                  width: 100%;
                  position: relative;
                  box-sizing: border-box;
                  margin: 0;
                  padding: 0;
                  /* See logged-in copy above for rationale on contain: paint. */
                  contain: paint;
                  isolation: isolate;
                  /* See logged-in copy for rationale on the 1px right-edge clip — kills the
                     residual sub-pixel sliver that varied per slide based on image content. */
                  clip-path: inset(0 1px 0 0);
                }
                .recent-trades-carousel-inner {
                  display: flex;
                  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
                  will-change: transform;
                  box-sizing: border-box;
                  margin: 0;
                  padding: 0;
                  /* See logged-in copy above for rationale on width: 100% — prevents
                     fractional implicit width that compounds sub-pixel drift over cycles. */
                  width: 100%;
                  max-width: 100%;
                  /* See logged-in copy for rationale on transform-origin and backface-visibility. */
                  transform-origin: 0 0;
                  backface-visibility: hidden;
                  -webkit-backface-visibility: hidden;
                }
                .recent-trades-carousel-item {
                  flex: 0 0 100%;
                  width: 100%;
                  min-width: 100%;
                  max-width: 100%;
                  box-sizing: border-box;
                  overflow: hidden;
                  position: relative;
                  margin: 0;
                  padding: 0;
                  flex-shrink: 0;
                }
                @media (max-width: 768px) {
                  .recent-trades-carousel-item {
                    overflow: hidden;
                  }
                  .recent-trades-carousel-wrapper {
                    overflow: hidden !important;
                  }
                }
                @media (max-width: 768px) {
                  .recent-trades-container {
                    width: 100% !important;
                    max-width: 100% !important;
                    padding: 0 1rem !important;
                    margin: 0 auto !important;
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: center !important;
                    text-align: center !important;
                    box-sizing: border-box !important;
                    overflow-x: hidden !important;
                  }
                  .recent-trades-carousel-wrapper {
                    width: 100% !important;
                    max-width: 100% !important;
                    overflow: hidden !important;
                    box-sizing: border-box !important;
                  }
                  .recent-trades-carousel-inner {
                    width: 100% !important;
                    max-width: 100% !important;
                    box-sizing: border-box !important;
                  }
                  .recent-trades-header {
                    font-size: 2.625rem !important;
                    margin-bottom: 1.5rem !important;
                    text-align: center !important;
                    width: 100% !important;
                  }
                  .recent-trades-images-container {
                    height: 200px !important;
                    width: 330px !important;
                    max-width: calc(100% - 2rem) !important;
                    margin: 0 auto 2rem auto !important;
                    position: relative !important;
                    left: 0 !important;
                    overflow: visible !important;
                    align-items: flex-start !important;
                    padding-top: 0 !important;
                    box-sizing: border-box !important;
                  }
                  .recent-trades-large-img {
                    width: 200px !important;
                    height: 200px !important;
                  }
                  .recent-trades-small-img {
                    left: 210px !important;
                  }
                  /* Direct-child only — multi-pedal stacks nest imgs inside .recent-trades-stack-small,
                     which must keep its own percentage-based sizing instead of being forced to 120x120. */
                  .recent-trades-small-img > img {
                    width: 120px !important;
                    height: 120px !important;
                  }
                  /* Multi-pedal stack wrappers — match the single-pedal sizes on each side
                     so the percentage-positioned cards inside scale identically. */
                  .recent-trades-stack-large {
                    width: 200px !important;
                    height: 200px !important;
                  }
                  .recent-trades-stack-small {
                    width: 120px !important;
                    height: 120px !important;
                  }
                  .recent-trades-arrow {
                    left: 175px !important;
                  }
                  .recent-trades-arrow img,
                  .recent-trades-arrow-img {
                    width: 60px !important;
                    height: 150px !important;
                    max-height: none !important;
                    object-fit: fill !important;
                  }
                  .recent-trades-info {
                    flex-direction: column !important;
                    align-items: center !important;
                    text-align: center !important;
                    padding-left: 0 !important;
                    max-width: 100% !important;
                    gap: 1rem !important;
                  }
                  .recent-trades-feedback {
                    text-align: center !important;
                    width: 100% !important;
                  }
                  .recent-trades-feedback p {
                    display: -webkit-box !important;
                    -webkit-line-clamp: 4 !important;
                    -webkit-box-orient: vertical !important;
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                  }
                }
              `}</style>
              <h3 className="recent-trades-header font-bold text-gray-900" style={{ 
                fontSize: '2.625rem', 
                fontWeight: '700', 
                marginBottom: '2rem',
                textAlign: 'center',
                letterSpacing: '-0.025em'
              }}>
                Recent Trades
              </h3>
              <div className="recent-trades-carousel-wrapper" ref={recentTradesCarouselRef}>
                <div 
                  className="recent-trades-carousel-inner"
                  style={{
                    // See logged-in copy above for rationale on the pixel-based transform + snap-on-wrap.
                    transform: recentTradesSlideWidth > 0
                      ? `translate3d(-${currentTradeIndex * recentTradesSlideWidth}px, 0, 0)`
                      : `translate3d(-${currentTradeIndex * 100}%, 0, 0)`,
                    transition: carouselSnapping ? 'none' : undefined
                  }}
                >
                  {successfulTrades.map((trade, index) => {
                    // Multi-pedal aware (see logged-in copy above for full context).
                    const receivedPedals = trade.pedalsReceived;
                    const givenPedals = trade.pedalsGiven;
                    const isCurrent = index === currentTradeIndex;
                    const isAdjacent = index === currentTradeIndex + 1;
                    const withImages = revealedSlides.has(index);
                    
                    return (
                      <div key={`trade-${trade.id}-${index}`} className="recent-trades-carousel-item" style={{ position: 'relative', width: '100%', maxWidth: '100%', overflow: 'hidden', flexShrink: 0 }}>
                        <div className="recent-trades-images-container" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', marginBottom: '2rem', position: 'relative', height: '336px', width: '100%', maxWidth: '100%', overflow: 'visible' }}>
                          {/* See logged-in copy above for rationale on calc(100% - 202px). */}
                          <div className="recent-trades-small-img" style={{ position: 'absolute', left: 'calc(100% - 202px)', top: 'calc(50% - 30px)', transform: 'translateY(-50%)', zIndex: 3 }}>
                            {renderPedalStack(givenPedals, 'small', { tradeId: trade.id, isCurrent, isAdjacent, withImages })}
                          </div>
                          <div style={{ position: 'relative', zIndex: 1 }}>
                            {renderPedalStack(receivedPedals, 'large', { tradeId: trade.id, isCurrent, isAdjacent, withImages })}
                          </div>
                          <div className="recent-trades-arrow" style={{ 
                            position: 'absolute',
                            left: '294px',
                            top: 'calc(50% - 30px)',
                            transform: 'translateY(-50%)',
                            zIndex: 4
                          }}>
                            <img 
                              src="/images/recent_trades_arrow.svg" 
                              alt="Double arrow" 
                              className="recent-trades-arrow-img"
                              style={{
                                width: '100px',
                                height: 'auto',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        </div>
                        <div className="recent-trades-info" style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', maxWidth: '553px', paddingLeft: '30px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', marginBottom: '0.6rem' }}>
                              {trade.reviewerProfile.username || 'Unknown'}
                            </div>
                            <div style={{ marginBottom: '0.6rem', transform: 'scale(1.0)' }}>
                              {trade.reviewerProfile.membershipTier && trade.reviewerProfile.membershipTier !== 'free' ? (
                                getMembershipBadge(trade.reviewerProfile.membershipTier)
                              ) : (
                                <div className="flex items-center bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
                                  Free Tier
                                </div>
                              )}
                            </div>
                            <div style={{ position: 'relative', width: '67px', height: '67px', marginBottom: '0.6rem' }}>
                              <img
                                key={`profile-${trade.id}`}
                                src={withImages ? (trade.reviewerProfile.photoURL || '/images/default-avatar.png') : BLANK_IMAGE_SRC}
                                alt={trade.reviewerProfile.username}
                                style={{
                                  width: '67px',
                                  height: '67px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '2px solid #D1D5DB'
                                }}
                              />
                              {getMembershipProfileBadge(trade.reviewerProfile.membershipTier)}
                              {(trade.reviewerProfile.badge || trade.reviewerProfile.completedTrades || 0) > 0 && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    bottom: '-5px',
                                    right: '-5px',
                                    backgroundColor: 'gold',
                                    color: '#000',
                                    fontWeight: 'bold',
                                    borderRadius: '50%',
                                    width: '22px',
                                    height: '22px',
                                    fontSize: '0.8rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid #fff',
                                    boxShadow: '0 0 5px rgba(0,0,0,0.3)'
                                  }}
                                >
                                  {trade.reviewerProfile.badge || trade.reviewerProfile.completedTrades || 0}
                                </div>
                              )}
                            </div>
                            {trade.reviewerProfile.averageRating > 0 ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem' }}>
                                <span style={{ color: '#F59E0B' }}>★</span>
                                <span style={{ fontWeight: '600', color: '#374151' }}>
                                  {trade.reviewerProfile.averageRating.toFixed(1)}
                                </span>
                              </div>
                            ) : (
                              <div style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>
                                No ratings yet
                              </div>
                            )}
                          </div>
                          <div className="recent-trades-feedback" style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '0.8rem' }}>
                              {Array.from({ length: 5 }).map((_, i) => (
                                <span key={i} style={{ color: '#F59E0B', fontSize: '1.4rem' }}>★</span>
                              ))}
                            </div>
                            <p style={{ 
                              fontSize: '0.9rem', 
                              color: '#374151', 
                              lineHeight: '1.5',
                              margin: 0,
                              fontStyle: 'italic',
                              textAlign: 'center',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              "{trade.reviewerFeedback.comment || 'Great trade!'}"
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Right Column - Mission Text */}
        <div className="mission-text-column" style={{ flex: isMobile ? 'none' : '1', textAlign: isMobile ? 'center' : 'left', marginLeft: isMobile ? '0' : missionMarginLeft, marginRight: isMobile ? '0' : '2rem', paddingTop: '3rem', paddingRight: isMobile ? '1rem' : '1rem', width: isMobile ? '100%' : 'auto' }}>
          <p className="text-gray-700" style={{ 
            fontSize: '22px',
            lineHeight: '1.6',
            marginBottom: '1.5rem'
          }}>
            <span style={{ color: '#C9A227', fontWeight: 'bold' }}>Trade Pedals. Not Dollars.</span>{' '}
            Your next favorite pedal is already out there. Trade the gear collecting dust for something that inspires you, at a fraction of the cost of buying new.
          </p>
          <p className="text-gray-700" style={{ 
            fontSize: '22px',
            lineHeight: '1.6',
            marginBottom: '1.5rem'
          }}>
            <span style={{ color: '#C9A227', fontWeight: 'bold' }}>Built to keep gear moving.</span>{' '}
            Trade, buy, or sell. PXC supports it all. Trading costs a flat $3. If you&apos;d rather buy or sell, our fees are half those of the leading marketplace. More money stays in your pocket either way.
          </p>
          <p className="text-gray-700" style={{ 
            fontSize: '22px',
            lineHeight: '1.6',
            marginBottom: '1.5rem'
          }}>
            <span style={{ color: '#C9A227', fontWeight: 'bold' }}>Every transaction is protected.</span>{' '}
            Verified users, secure Stripe payments, insured shipping, and optional TradeProtect coverage. We&apos;ve handled the hard parts so you can focus on the fun part—the gear.
          </p>
          <div className="mission-spacer" style={{ height: '6rem' }}></div>
          <div style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
            <p className="text-gray-600" style={{ 
              fontSize: '18px',
              lineHeight: '1.6',
              textAlign: 'center'
            }}>
              For added benefits and features like reduced fees and promoted match listings, visit the <Link to="/profile" className="hover:underline font-medium" style={{ color: '#5A9ECB' }}>profile</Link> page to become a paid member.
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );

  // Auth not yet known: render only the page background at the dashboard's
  // height so the footer does not jump, and neither the signed-out nor the
  // signed-in experience is painted and then replaced. Firebase reports the
  // persisted session right after load, so this state lasts a fraction of a
  // second; it is not a splash and nothing is loaded behind it.
  if (!authResolved) {
    return <div className="dashboard-auth-pending" aria-busy="true" style={{ minHeight: '100vh', backgroundColor: '#F7F3EB' }} />;
  }

  return (
    <>
      {dashboardTip && <OnboardingTip key={dashboardTip} tipId={dashboardTip} onGotIt={dismissDashboardTip} delay={dashboardTipDelay} />}
      <SEO
        title="Trade Guitar Pedals Online | Buy, Sell & Trade Effects Pedals"
        description="Trade guitar pedals online with musicians worldwide. Buy, sell, and trade effects pedals safely with escrow protection. Find your next pedal trade today!"
        keywords="trade guitar pedals, buy guitar pedals, sell effects pedals, trade effects pedals, guitar pedal marketplace, pedal trading, stompbox trading, boutique pedals, vintage pedals, effects pedal trading"
        url="https://pedalxchange.com"
        structuredData={[structuredData, organizationStructuredData]}
      />
      <div className="min-h-screen" style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
        {/* Background section */}
        <div style={{ backgroundColor: '#F7F3EB', flex: 1, overflowX: 'hidden' }}>

      {/* Conditionally render sections based on user state */}
      {user ? (
        <>
          <ActiveTradesPurchasesStrip user={user} onVisibilityChange={setActiveTradesStripVisible} />
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', width: '100%', boxSizing: 'border-box' }}>
            <SellingDisabledBanner mode="full" />
          </div>
          <style>{`
            @media (max-width: 768px) {
              .dashboard-new-feature-banner {
                margin: 0.5rem auto 0 auto !important;
              }
            }
          `}</style>
          <DashboardAnnouncementBanner user={user} />

          {/* Logged-in dashboard: carousel order follows want/rec content (see loggedInCarouselLayout) */}
          {loggedInCarouselLayout === 'full' && (
            <>
              <div className="match-opportunities-wrapper" style={{ marginTop: '1rem' }}>
                <style>{`
                  @media (max-width: 768px) {
                    .match-opportunities-wrapper {
                      margin-top: 0.35rem !important;
                    }
                  }
                `}</style>
                {recommendedPedalsSection}
              </div>
              <div className="dashboard-logged-in-want-after-rec" style={{ marginTop: '-2rem' }}>
                {wantListMatchesSection}
              </div>
              <div className="dashboard-logged-in-latest-after-want" style={{ marginTop: '-1rem' }}>{latestPedalsSection}</div>
              <DashboardPedalNewsStrip />
              {loggedInMissionSection}
              {loggedInHeroWrap}
              {howItWorksSection}
            </>
          )}
          {loggedInCarouselLayout === 'noRec' && (
            <>
              <div className="want-list-matches-wrapper" style={{ marginTop: '1rem' }}>
                <style>{`
                  @media (max-width: 768px) {
                    .want-list-matches-wrapper {
                      margin-top: 0.35rem !important;
                    }
                  }
                `}</style>
                {wantListMatchesSection}
              </div>
              <div style={{ marginTop: '-2rem' }}>{latestPedalsSection}</div>
              <DashboardPedalNewsStrip />
              {loggedInMissionSection}
              {loggedInHeroWrap}
              {howItWorksSection}
              <div style={{ marginTop: '2rem' }}>{recommendedPedalsSection}</div>
            </>
          )}
          {loggedInCarouselLayout === 'noWant' && (
            <>
              <div style={{ marginTop: '1rem' }}>{recommendedPedalsSection}</div>
              <div style={{ marginTop: '-2rem' }}>{latestPedalsSection}</div>
              <DashboardPedalNewsStrip />
              {loggedInMissionSection}
              {loggedInHeroWrap}
              {howItWorksSection}
              <div className="want-list-matches-wrapper" style={{ marginTop: '2rem' }}>
                {wantListMatchesSection}
              </div>
            </>
          )}
          {loggedInCarouselLayout === 'neither' && (
            <>
              <div style={{ marginTop: '1rem' }}>{latestPedalsSection}</div>
              <DashboardPedalNewsStrip />
              {howItWorksSection}
              {loggedInMissionSection}
              {loggedInHeroWrap}
              <div className="want-list-matches-wrapper" style={{ marginTop: '2rem' }}>
                {wantListMatchesSection}
              </div>
              <div style={{ marginTop: '1rem' }}>{recommendedPedalsSection}</div>
            </>
          )}
        </>
      ) : (
        <>
          {/* Logged-out: Hero (+ mission in hero) → How it works → Latest pedals → Latest pedal news */}
          {heroBannerSection}
          {howItWorksSection}
          {latestPedalsSection}
          <DashboardPedalNewsStrip showDividerAbove />
          {recommendedPedalsSection}
        </>
      )}

      {/* Fixed gap below content */}
      <div style={{ height: '200px' }}></div>
        </div>
      
      {/* Footer */}
      <Footer isDashboard={true} />

      {/* Account Checklist - Fixed position widget */}
      <AccountChecklist />

      {/* Back to Top Button. Hidden on native: its fixed bottom-right position
          overlaps the Account Checklist's expand/collapse caret on the dashboard.
          Web keeps it (no tab bar / different layout, so no overlap). */}
      {showBackToTop && !Capacitor.isNativePlatform() && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8AB0';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}
      </div>
    </>
  );
};

const PedalCard = ({ pedal, currentUser, firestoreUser, variant = 'default', isPotentialMatch = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Real photos in listing order, else exactly the placeholder (plan §4).
  const images = listingImageUrls(pedal);

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <Link
      to={`/listing/${pedal.id}`}
      className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden"
      style={{
        display: 'block',
        width: 331,
        textDecoration: 'none',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
        border: '1px solid #e5e7eb'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image - fixed size with carousel and hover overlay. Sits at the top of the card. */}
      <div className="bg-gray-100 relative flex items-center justify-center" style={{ borderRadius: '15px', overflow: 'hidden', width: 331, height: 331, padding: '8px', position: 'relative', boxSizing: 'border-box' }}>
        {images.length > 0 ? (
          <>
            <ListingThumb
              src={images[currentImageIndex]}
              alt={`${pedal.make} ${pedal.model}`}
              className="w-full h-full object-cover"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }}
              key={currentImageIndex}
            />
            
            {/* Navigation Arrows */}
            <style jsx>{`
              .dashboard-nav-arrow {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                background: rgba(255, 255, 255, 0.82);
                border: none;
                color: #000000;
                font-size: 26px;
                cursor: pointer;
                width: 38px;
                height: 38px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                z-index: 2;
                opacity: 0;
                transition: opacity 0.2s, background 0.2s;
                user-select: none;
              }

              .dashboard-nav-arrow-left {
                left: 6px;
                padding-bottom: 3px;
              }

              .dashboard-nav-arrow-right {
                right: 6px;
                padding-bottom: 3px;
              }

              .dashboard-nav-arrow:hover {
                background: rgba(255, 255, 255, 0.96);
              }

              /* Show arrows on hover for desktop */
              @media (hover: hover) and (pointer: fine) {
                .dashboard-image-container:hover .dashboard-nav-arrow {
                  opacity: 1;
                }
              }

              /* Always show arrows on mobile */
              @media (hover: none) or (pointer: coarse) {
                .dashboard-nav-arrow {
                  opacity: 0.9;
                }
              }
              
              /* Description overlay - desktop only.
                 No fixed height: shrink-wraps to the (line-clamped) text +
                 padding so the ellipsis on line 3 always renders. z-index 3
                 keeps it above lower-left badges (Potential Match etc.). */
              .dashboard-description-overlay {
                position: absolute;
                bottom: 8px;
                left: 8px;
                right: 8px;
                background: rgba(31, 41, 55, 0.80);
                padding: 12px 16px;
                transform: translateY(calc(100% + 24px));
                transition: transform 0.25s ease-in-out;
                display: flex;
                align-items: flex-start;
                z-index: 3;
                border-radius: 12px;
                will-change: transform;
              }
              
              @media (hover: hover) and (pointer: fine) {
                .dashboard-image-container:hover .dashboard-description-overlay {
                  transform: translateY(0);
                }
              }
              
              .dashboard-description-text {
                color: #ffffff;
                font-size: 14px;
                font-weight: bold;
                line-height: 1.4;
                margin: 0;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
                word-wrap: break-word;
                overflow-wrap: break-word;
                max-height: calc(1.4em * 3);
                width: 100%;
              }
            `}</style>
            
            <div className="dashboard-image-container" style={{ position: 'absolute', inset: 0 }}>
              {/* Left Arrow */}
              <button
                className="dashboard-nav-arrow dashboard-nav-arrow-left"
                onClick={handlePrevImage}
                aria-label="Previous image"
                style={{ pointerEvents: 'auto' }}
              >
                ‹
              </button>
              
              {/* Right Arrow */}
              <button
                className="dashboard-nav-arrow dashboard-nav-arrow-right"
                onClick={handleNextImage}
                aria-label="Next image"
                style={{ pointerEvents: 'auto' }}
              >
                ›
              </button>
              
              {/* Description Overlay - Desktop Only, First Image Only */}
              {pedal.description && currentImageIndex === 0 && (
                <div className="dashboard-description-overlay">
                  <p className="dashboard-description-text">{pedal.description}</p>
                </div>
              )}
            </div>
            
            {/* Active Trader Badge (if owner is weekly active) */}
            {currentImageIndex === 0 && <ActiveTraderBadge user={pedal.owner} />}
            {/* Photos coming (plan §4): same badge as the other dashboard tiles */}
            {currentImageIndex === 0 && isPlaceholderListing(pedal) && (
                <div
                  title="The owner listed this pedal without photos. Real photos are required before an offer can be accepted."
                  style={{ position: 'absolute', top: '16px', left: '16px', background: 'rgba(255,255,255,0.95)', color: '#4A5759', fontFamily: 'Nunito, sans-serif', fontSize: '13px', fontWeight: 700, letterSpacing: '0.3px', padding: '6px 12px', borderRadius: '9999px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 10, pointerEvents: 'none' }}
                >
                  Photos coming
                </div>
              )}

            {/* Potential Match — full-width banner pinned to the bottom of the
                image when the listing owner also wants one of OUR pedals.
                Same ribbon treatment used on LatestPedals + WantListMatches +
                Recommendations. On the dashboard the sibling "Wants your X"
                banner runs at 14px / 8px 12px, so this one matches it tile-
                for-tile (14px / 8px 12px desktop). Mobile override lives in
                index.css under .dashboard-carousel-card .potential-match-banner
                to compensate for the parent card's zoom: 0.75. */}
            {currentImageIndex === 0 && isPotentialMatch && (
              <div
                className="potential-match-banner"
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '8px',
                  right: '8px',
                  backgroundColor: 'rgba(46, 125, 50, 0.92)',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 700,
                  textAlign: 'center',
                  padding: '8px 12px',
                  borderRadius: '0 0 8px 8px',
                  fontFamily: 'Nunito, sans-serif',
                  zIndex: 2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  pointerEvents: 'none'
                }}
              >
                Potential Match
              </div>
            )}

            <div className="absolute top-2 right-2" style={{ zIndex: 3 }}>
              {pedal.owner.badge && (
                <div className="bg-yellow-400 text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border border-white">
                  {pedal.owner.badge}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ width: 331, height: 331 }}>
            <img
              src={placeholderFor(pedal).url}
              alt="Photos coming"
              className="w-full h-full"
              style={{ objectFit: 'cover', opacity: 0.85 }}
              style={{ borderRadius: '15px !important' }}
            />
          </div>
        )}
      </div>

      {/* Info section — brand (small uppercase), model (bold), price + condition pill row */}
      <div className="dashboard-pedal-card-info" style={{ backgroundColor: '#ffffff', padding: '12px 14px 14px', textAlign: 'left' }}>
        <div title={pedal.make} className="dashboard-pedal-card-brand" style={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '2px' }}>
          {pedal.make}
        </div>
        <div title={pedal.model} className="dashboard-pedal-card-model" style={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '8px' }}>
          {pedal.model}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <PedalPriceDisplay
            price={Number(pedal.price)}
            previousPrice={pedal.previousPrice != null ? Number(pedal.previousPrice) : null}
            layout="row"
            className="dashboard-pedal-card-price"
            dashboardTile
          />
          {variant === 'want-list-match' ? (
            <span className="dashboard-pedal-card-pill" style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '9999px', backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Matches want
            </span>
          ) : (
            <span className="dashboard-pedal-card-pill" style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '9999px', backgroundColor: '#F3F4F6', color: '#374151', fontWeight: 700, whiteSpace: 'nowrap' }}>
              {pedal.condition}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default UserDashboard;

